/**
 * Node Registry - Workflow nodes for code transformation pipeline
 * Generic/language-agnostic design
 */

// Category colors
export const CATEGORY_COLORS = {
  templates: '#f59e0b',   // Amber - pre-built workflow templates
  pipeline: '#10b981',    // Emerald - pre-configured API stages
  ingest: '#58a6ff',      // Blue - getting files in
  parse: '#3fb950',       // Green - parsing source
  analyze: '#a371f7',     // Purple - code analysis
  refactor: '#d29922',    // Yellow - code refactoring
  map: '#db6d28',         // Orange - dependency mapping
  decompose: '#f778ba',   // Pink - monolith decomposition
  data: '#79c0ff',        // Light blue - data analysis
  discover: '#a5d6ff',    // Lighter blue - discovery
  architecture: '#9333ea', // Purple - cloud architecture
  generate: '#7ee787',    // Light green - code generation
  output: '#8b949e',      // Gray - export/output
  ai: '#00d4aa',          // Teal/Cyan - AI-powered operations
  control: '#6b7280',     // Gray - workflow control/utilities
}

// =============================================================================
// NODE DEFINITIONS
// =============================================================================

export const NODE_REGISTRY = {
  // ===========================================================================
  // TEMPLATES - Pre-built workflow templates (expand into multiple nodes)
  // ===========================================================================
  'template-full-pipeline': {
    type: 'template-full-pipeline',
    category: 'templates',
    label: 'Full Pipeline',
    description: 'Complete transformation pipeline: Upload → Code Analysis → Refactor → AI Naming → Dependencies → Monolith → Data → Discovery → Architecture → Service Gen → Java Analysis → Pre-Flight → Java Package',
    icon: 'Workflow',
    color: CATEGORY_COLORS.templates,
    isTemplate: true,  // Special flag for template nodes
    templateNodes: [
      'pipeline-upload',
      'pipeline-code-analysis',
      'pipeline-refactor',
      'pipeline-ai-naming',
      'pipeline-dependencies',
      'pipeline-monolith',
      'pipeline-data',
      'pipeline-discovery',
      'pipeline-architecture',
      'pipeline-business-logic',
      'pipeline-code-translation',
      'pipeline-service-gen',
      'pipeline-java-analysis',
      'pipeline-pre-flight',
      'pipeline-java-package',
    ],
    inputs: [],
    outputs: [],
    configSchema: [],
    defaultConfig: {},
  },

  'template-idms-full-path': {
    type: 'template-idms-full-path',
    category: 'templates',
    label: 'IDMS Full Path',
    description: 'Complete IDMS modernization pipeline: Upload → IDMS Analysis → IDMS Compatibility → COBOL Version Upgrade → IDMS Data Architecture → IDMS Assessment → SQL Generation',
    icon: 'Workflow',
    color: CATEGORY_COLORS.templates,
    isTemplate: true,
    templateNodes: [
      'pipeline-upload',
      'pipeline-idms-analysis',
      'pipeline-idms-compatibility',
      'pipeline-cobol-version-upgrade',
      'pipeline-idms-data-architecture',
      'pipeline-idms-assessment',
      'pipeline-idms-sql-generation',
    ],
    inputs: [],
    outputs: [],
    configSchema: [],
    defaultConfig: {},
  },

  // ===========================================================================
  // PIPELINE - Pre-configured API-mapped transformation stages
  // ===========================================================================
  'pipeline-upload': {
    type: 'pipeline-upload',
    disabled: true,
    category: 'pipeline',
    label: 'Upload Files',
    description: 'Upload ZIP archive or folder of source files',
    icon: 'Upload',
    color: CATEGORY_COLORS.pipeline,
    apiEndpoint: '/ingest/upload',
    apiMethod: 'POST',
    inputs: [],
    outputs: [
      { id: 'files', name: 'Source Files', type: 'files' },
      { id: 'source_hash', name: 'Source Hash', type: 'string' },
      { id: 'job_id', name: 'Job ID', type: 'string' },
    ],
    configSchema: [
      { key: 'accept', label: 'File Types', type: 'text', default: '.zip,.tar,.gz', section: 'General' },
      { key: 'sourceLanguage', label: 'Source Language', type: 'select', options: [
        { value: 'cobol', label: 'COBOL' },
        { value: 'pli', label: 'PL/I' },
        { value: 'natural', label: 'Natural' },
        { value: 'rpg', label: 'RPG' },
      ], default: 'cobol', section: 'Languages' },
      { key: 'targetLanguage', label: 'Target Language', type: 'select', options: [
        { value: 'java', label: 'Java' },
        { value: 'csharp', label: 'C#' },
        { value: 'python', label: 'Python' },
        { value: 'typescript', label: 'TypeScript' },
      ], default: 'java', section: 'Languages' },
      { key: 'generateTypeMappings', label: 'Generate Type Mappings', type: 'boolean', default: true, section: 'Options' },
      { key: 'autoExtract', label: 'Auto-extract Archives', type: 'boolean', default: true, section: 'Options' },
      { key: 'encoding', label: 'File Encoding', type: 'select', options: [
        { value: 'auto', label: 'Auto-detect' },
        { value: 'utf8', label: 'UTF-8' },
        { value: 'ebcdic', label: 'EBCDIC' },
        { value: 'ascii', label: 'ASCII' },
      ], default: 'auto', section: 'Options' },
      { key: 'includePatterns', label: 'Include Patterns', type: 'text', placeholder: '*.cbl, *.cpy, *.pli', section: 'Filtering' },
      { key: 'excludePatterns', label: 'Exclude Patterns', type: 'text', placeholder: '*.bak, *.tmp', section: 'Filtering' },
    ],
    defaultConfig: { accept: '.zip,.tar,.gz', sourceLanguage: 'cobol', targetLanguage: 'java', generateTypeMappings: true, autoExtract: true, encoding: 'auto', includePatterns: '', excludePatterns: '' },
  },
  'pipeline-code-analysis': {
    type: 'pipeline-code-analysis',
    disabled: true,
    category: 'pipeline',
    label: 'Code Analysis',
    description: 'Parse source code and generate target language',
    icon: 'FileCode',
    color: CATEGORY_COLORS.pipeline,
    apiEndpoint: '/codeanalysis',
    apiMethod: 'POST',
    inputs: [{ id: 'files', name: 'Source Files', type: 'files', required: true }],
    outputs: [
      { id: 'analysis', name: 'Analysis Results', type: 'json' },
      { id: 'generated', name: 'Generated Code', type: 'files' },
    ],
    configSchema: [
      { key: 'main_program', label: 'Main Program', type: 'text', placeholder: 'Auto-detect', section: 'General' },
      { key: 'source_lang', label: 'Source Language', type: 'select', options: [
        { value: 'cobol', label: 'COBOL' },
        { value: 'pli', label: 'PL/I' },
        { value: 'natural', label: 'Natural' },
        { value: 'rpg', label: 'RPG' },
      ], default: 'cobol', section: 'Languages' },
      { key: 'target_lang', label: 'Target Language', type: 'select', options: [
        { value: 'java', label: 'Java' },
        { value: 'csharp', label: 'C#' },
        { value: 'python', label: 'Python' },
        { value: 'typescript', label: 'TypeScript' },
      ], default: 'java', section: 'Languages' },
      { key: 'generate_java', label: 'Generate Java Code', type: 'boolean', default: true, section: 'Options' },
      { key: 'generate_graphs', label: 'Generate Call Graphs', type: 'boolean', default: true, section: 'Options' },
      { key: 'generate_inventory', label: 'Generate Line Inventory', type: 'boolean', default: true, section: 'Options' },
    ],
    defaultConfig: { source_lang: 'cobol', target_lang: 'java', generate_java: true, generate_graphs: true, generate_inventory: true },
  },
  'pipeline-idms-analysis': {
    type: 'pipeline-idms-analysis',
    disabled: true,
    category: 'pipeline',
    label: 'IDMS Analysis',
    description: 'Scan COBOL programs for IDMS database usage — identifies data operations, schema inventory, copybook layouts, and program structure to support migration planning.',
    icon: 'Database',
    color: CATEGORY_COLORS.pipeline,
    apiEndpoint: '/idmsanalysis',
    apiMethod: 'POST',
    inputs: [{ id: 'files', name: 'Source Files', type: 'files', required: true }],
    outputs: [
      { id: 'analysis', name: 'IDMS Analysis Results', type: 'json' },
      { id: 'graph', name: 'IDMS Graph Nodes', type: 'json' },
      { id: 'job_id', name: 'Job ID', type: 'string' },
    ],
    configSchema: [],
    defaultConfig: {},
  },
  'pipeline-idms-compatibility': {
    type: 'pipeline-idms-compatibility',
    disabled: true,
    category: 'pipeline',
    label: 'IDMS Compatibility',
    description: 'Check COBOL programs for compatibility issues between compiler versions. Detects breaking patterns and flags each as auto-fixable or requiring manual review.',
    icon: 'ShieldCheck',
    color: CATEGORY_COLORS.pipeline,
    apiEndpoint: '/idmscompatibility',
    apiMethod: 'POST',
    inputs: [{ id: 'analysis', name: 'IDMS Analysis Results', type: 'json', required: true }],
    outputs: [
      { id: 'findings', name: 'CMPR2 Findings', type: 'json' },
      { id: 'scanner_results', name: 'Scanner Results', type: 'json' },
      { id: 'job_id', name: 'Job ID', type: 'string' },
    ],
    configSchema: [],
    defaultConfig: {},
  },
  'pipeline-cobol-version-upgrade': {
    type: 'pipeline-cobol-version-upgrade',
    disabled: true,
    category: 'pipeline',
    label: 'COBOL Version Upgrade',
    description: 'Upgrade COBOL source to the target compiler version. Applies automated fixes from the compatibility scan and generates upgraded source with change reports and updated JCL.',
    icon: 'ArrowUpCircle',
    color: CATEGORY_COLORS.pipeline,
    apiEndpoint: '/cobolversionupgrade',
    apiMethod: 'POST',
    inputs: [
      { id: 'analysis', name: 'IDMS Analysis Results', type: 'json', required: true },
      { id: 'compatibility', name: 'Compatibility Scan Results', type: 'json', required: true },
    ],
    outputs: [
      { id: 'v6_output', name: 'Upgraded COBOL Output', type: 'files' },
      { id: 'changes', name: 'Transform Changes', type: 'json' },
      { id: 'job_id', name: 'Job ID', type: 'string' },
    ],
    configSchema: [],
    defaultConfig: {},
  },
  'pipeline-idms-data-architecture': {
    type: 'pipeline-idms-data-architecture',
    disabled: true,
    category: 'pipeline',
    label: 'IDMS Data Architecture',
    description: 'Map out the database schema and record relationships from how programs access data. Produces a visual relationship diagram for migration planning.',
    icon: 'GitBranch',
    color: CATEGORY_COLORS.pipeline,
    apiEndpoint: '/idmsdataarchitecture',
    apiMethod: 'POST',
    inputs: [{ id: 'analysis', name: 'IDMS Analysis Results', type: 'json', required: true }],
    outputs: [
      { id: 'schema', name: 'Inferred Schema', type: 'json' },
      { id: 'graph', name: 'Data Architecture Graph', type: 'json' },
      { id: 'job_id', name: 'Job ID', type: 'string' },
    ],
    configSchema: [],
    defaultConfig: {},
  },
  'pipeline-idms-assessment': {
    type: 'pipeline-idms-assessment',
    disabled: true,
    category: 'pipeline',
    label: 'IDMS Assessment',
    description: 'Score migration readiness per program. Evaluates data complexity, code complexity, coupling, and compatibility to produce effort estimates and a recommended migration sequence.',
    icon: 'ClipboardCheck',
    color: CATEGORY_COLORS.pipeline,
    apiEndpoint: '/idmsassessment',
    apiMethod: 'POST',
    inputs: [
      { id: 'analysis', name: 'IDMS Analysis Results', type: 'json', required: true },
      { id: 'compatibility', name: 'IDMS Compatibility Results', type: 'json', required: false },
    ],
    outputs: [
      { id: 'assessment', name: 'Assessment Summary', type: 'json' },
      { id: 'risks', name: 'Program Risk Scores', type: 'json' },
      { id: 'job_id', name: 'Job ID', type: 'string' },
    ],
    configSchema: [],
    defaultConfig: {},
  },
  'pipeline-idms-sql-generation': {
    type: 'pipeline-idms-sql-generation',
    disabled: true,
    category: 'pipeline',
    label: 'IDMS SQL Generation',
    description: 'Generate table definitions and stored procedures for your target database. Supports SQL Server, DB2, and Oracle. Maps COBOL data types to SQL columns with proper keys and relationships.',
    icon: 'Database',
    color: CATEGORY_COLORS.pipeline,
    apiEndpoint: '/idmssqlgeneration',
    apiMethod: 'POST',
    inputs: [
      { id: 'analysis', name: 'IDMS Analysis Results', type: 'json', required: true },
      { id: 'data_architecture', name: 'IDMS Data Architecture Results', type: 'json', required: true },
    ],
    outputs: [
      { id: 'ddl', name: 'Full DDL Script', type: 'text' },
      { id: 'dml', name: 'Stored Procedures Script', type: 'text' },
      { id: 'tables', name: 'Table Details', type: 'json' },
      { id: 'job_id', name: 'Job ID', type: 'string' },
    ],
    configSchema: [
      {
        key: 'dialect',
        label: 'SQL Dialect',
        type: 'select',
        options: ['SQL Server', 'DB2', 'Oracle'],
        default: 'SQL Server',
      },
    ],
    defaultConfig: { dialect: 'SQL Server' },
  },
  'pipeline-ai-naming': {
    type: 'pipeline-ai-naming',
    disabled: true,
    category: 'pipeline',
    label: 'AI Naming',
    description: 'Rename methods and services with meaningful Java names using AI. Full traceability back to original COBOL paragraphs.',
    icon: 'Tag',
    color: CATEGORY_COLORS.pipeline,
    apiEndpoint: '/ainaming',
    apiMethod: 'POST',
    inputs: [{ id: 'refactor', name: 'Code Refactor Output', type: 'json', required: true }],
    outputs: [
      { id: 'naming_suggestions', name: 'Naming Suggestions', type: 'json' },
      { id: 'cobol_mapping', name: 'COBOL Traceability', type: 'json' },
    ],
    configSchema: [
      { key: 'enable_ai', label: 'Enable AI Naming', type: 'boolean', default: true, section: 'General', description: 'Use AI to suggest semantic method/service names' },
      { key: 'confidence_threshold', label: 'Confidence Threshold', type: 'number', default: 0.75, min: 0.0, max: 1.0, step: 0.05, section: 'Options', description: 'Minimum confidence score (0.0-1.0)' },
      { key: 'auto_approve_high_confidence', label: 'Auto-approve High Confidence (>0.90)', type: 'boolean', default: false, section: 'Options', description: 'Automatically approve suggestions with confidence > 0.90' },
    ],
    defaultConfig: { enable_ai: true, confidence_threshold: 0.75, auto_approve_high_confidence: false },
  },
  'pipeline-refactor': {
    type: 'pipeline-refactor',
    disabled: true,
    category: 'pipeline',
    label: 'Code Refactor',
    description: 'Analyze generated Java code for refactoring opportunities. Detects patterns, suggests class splitting, and can transform code.',
    icon: 'Wand2',
    color: CATEGORY_COLORS.pipeline,
    apiEndpoint: '/coderefactor',
    apiMethod: 'POST',
    inputs: [{ id: 'analysis', name: 'Code Analysis Results', type: 'json', required: true }],
    outputs: [
      { id: 'report', name: 'Refactor Report', type: 'json' },
      { id: 'recipes', name: 'Refactor Recipes', type: 'json' },
      { id: 'refactored', name: 'Refactored Code', type: 'files' },
    ],
    configSchema: [
      // General settings
      { key: 'class_name', label: 'Class Name (optional)', type: 'text', placeholder: 'All classes (leave empty)', section: 'General' },
      // AI settings
      { key: 'use_ai', label: 'Use AI Analysis (Claude)', type: 'boolean', default: true, section: 'Options' },
      // Transform - applies recipes and creates new Java files in separate location
      { key: 'apply_transformations', label: 'Apply Transformations', type: 'boolean', default: true, section: 'Options', description: 'Create refactored Java files (non-destructive)' },
      // Recipe filter (only relevant when apply_transformations is true)
      { key: 'recipes_to_apply', label: 'Recipes to Apply', type: 'select', options: [
        { value: 'all', label: 'All Recipes' },
        { value: 'high_confidence', label: 'High Confidence Only (>90%)' },
        { value: 'extract_class', label: 'Extract Class Only' },
      ], default: 'all', section: 'Options' },
    ],
    defaultConfig: { use_ai: true, apply_transformations: true, recipes_to_apply: 'all' },
  },
  'pipeline-dependencies': {
    type: 'pipeline-dependencies',
    category: 'pipeline',
    label: 'Dependency Mapper',
    description: 'Build dependency graphs and analyze relationships',
    icon: 'GitFork',
    color: CATEGORY_COLORS.pipeline,
    apiEndpoint: '/dependencymapper',
    apiMethod: 'POST',
    inputs: [{ id: 'analysis', name: 'Analysis Results', type: 'json', required: true }],
    outputs: [{ id: 'graph', name: 'Dependency Graph', type: 'graph' }],
    configSchema: [
      { key: 'analysis_type', label: 'Analysis Type', type: 'select', options: [
        { value: 'cobol', label: 'COBOL Dependencies' },
        { value: 'java', label: 'Java Dependencies' },
      ], default: 'cobol', section: 'General' },
      { key: 'include_externals', label: 'Include External Calls', type: 'boolean', default: true, section: 'Options' },
      { key: 'missing_symbol_default_action', label: 'Missing Symbols Default Action', type: 'select', options: [
        { value: 'compile-only-stub', label: 'Generate compile-only stubs (default)' },
        { value: 'fix-now', label: 'Fix now (open editor)' },
        { value: 'mark-external', label: 'Mark as external dependency' },
      ], default: 'compile-only-stub', section: 'Options' },
    ],
    defaultConfig: { analysis_type: 'cobol', include_externals: true, missing_symbol_default_action: 'compile-only-stub' },
  },
  'pipeline-monolith': {
    type: 'pipeline-monolith',
    disabled: true,
    category: 'pipeline',
    label: 'Monolith Identifier',
    description: 'Detect anti-patterns and decomposition opportunities',
    icon: 'Scissors',
    color: CATEGORY_COLORS.pipeline,
    apiEndpoint: '/monolithidentifier',
    apiMethod: 'POST',
    inputs: [{ id: 'analysis', name: 'Analysis Results', type: 'json', required: true }],
    outputs: [
      { id: 'patterns', name: 'Detected Patterns', type: 'json' },
      { id: 'decomposition', name: 'Decomposition Strategy', type: 'json' },
    ],
    configSchema: [
      { key: 'analysis_type', label: 'Analysis Type', type: 'select', options: [
        { value: 'cobol', label: 'COBOL Analysis' },
        { value: 'java', label: 'Java Analysis' },
      ], default: 'cobol', section: 'General' },
    ],
    defaultConfig: { analysis_type: 'cobol' },
  },
  'pipeline-data': {
    type: 'pipeline-data',
    disabled: true,
    category: 'pipeline',
    label: 'Data Analysis',
    description: 'Analyze data structures, relationships, and database schemas',
    icon: 'Database',
    color: CATEGORY_COLORS.pipeline,
    apiEndpoint: '/dataanalysis',
    apiMethod: 'POST',
    inputs: [{ id: 'analysis', name: 'Analysis Results', type: 'json', required: true }],
    outputs: [
      { id: 'erd', name: 'ERD', type: 'json' },
      { id: 'lineage', name: 'Data Lineage', type: 'json' },
    ],
    configSchema: [
      { key: 'generate_erd', label: 'Generate ERD', type: 'boolean', default: true, section: 'Options' },
      { key: 'generate_ddl', label: 'Generate DDL', type: 'boolean', default: true, section: 'Options' },
    ],
    defaultConfig: { generate_erd: true, generate_ddl: true },
  },
  'pipeline-discovery': {
    type: 'pipeline-discovery',
    disabled: true,
    category: 'pipeline',
    label: 'Discovery & ROI',
    description: 'Generate ROI analysis, migration roadmap, and business process discovery',
    icon: 'TrendingUp',
    color: CATEGORY_COLORS.pipeline,
    apiEndpoint: '/discovery/analyze',
    apiMethod: 'POST',
    inputs: [{ id: 'analysis', name: 'Analysis Results', type: 'json', required: true }],
    outputs: [
      { id: 'roi', name: 'ROI Analysis', type: 'json' },
      { id: 'roadmap', name: 'Migration Roadmap', type: 'json' },
      { id: 'processes', name: 'Business Processes', type: 'json' },
    ],
    configSchema: [
      { key: 'cobol_dev_count', label: 'COBOL Developer Count', type: 'number', default: 5, section: 'ROI Inputs' },
      { key: 'cobol_dev_salary', label: 'Developer Salary ($)', type: 'number', default: 124681, section: 'ROI Inputs' },
      { key: 'discount_rate', label: 'Discount Rate (%)', type: 'number', default: 8, section: 'ROI Inputs' },
      { key: 'mainframe_cost', label: 'Annual Mainframe Cost ($)', type: 'number', default: 50000, section: 'ROI Inputs' },
    ],
    defaultConfig: { cobol_dev_count: 5, cobol_dev_salary: 124681, discount_rate: 8, mainframe_cost: 50000 },
  },
  'pipeline-architecture': {
    type: 'pipeline-architecture',
    disabled: true,
    category: 'pipeline',
    label: 'Architecture',
    description: 'Generate AWS recommendations and Infrastructure as Code',
    icon: 'Cloud',
    color: CATEGORY_COLORS.pipeline,
    apiEndpoint: '/architecture/analyze',
    apiMethod: 'POST',
    inputs: [{ id: 'analysis', name: 'Analysis Results', type: 'json', required: true }],
    outputs: [
      { id: 'recommendations', name: 'AWS Recommendations', type: 'json' },
      { id: 'iac', name: 'IaC Templates', type: 'files' },
    ],
    configSchema: [
      { key: 'cloud_provider', label: 'Cloud Provider', type: 'select', options: [
        { value: 'aws', label: 'AWS' },
        { value: 'azure', label: 'Azure' },
        { value: 'gcp', label: 'Google Cloud' },
      ], default: 'aws', section: 'General' },
      { key: 'iac_type', label: 'IaC Type', type: 'select', options: [
        { value: 'cdk', label: 'AWS CDK' },
        { value: 'cloudformation', label: 'CloudFormation' },
        { value: 'terraform', label: 'Terraform' },
      ], default: 'cdk', section: 'General' },
    ],
    defaultConfig: { cloud_provider: 'aws', iac_type: 'cdk' },
  },
  'pipeline-business-logic': {
    type: 'pipeline-business-logic',
    disabled: true,
    category: 'pipeline',
    label: 'Business Logic',
    description: 'Analyze COBOL business logic semantics — ordered execution steps, data transformations, business rules, error handling. Feeds into Service Generation for richer Java output.',
    icon: 'BookOpen',
    color: CATEGORY_COLORS.pipeline,
    apiEndpoint: '/businesslogic',
    apiMethod: 'POST',
    inputs: [
      { id: 'codeanalysis', name: 'Code Analysis', type: 'json', required: true },
    ],
    outputs: [
      { id: 'specs', name: 'Business Specs', type: 'json' },
      { id: 'graph', name: 'Graph Updates', type: 'json' },
      { id: 'job_id', name: 'Job ID', type: 'string' },
    ],
    configSchema: [],
    defaultConfig: {},
  },
  'pipeline-code-translation': {
    type: 'pipeline-code-translation',
    disabled: false,
    category: 'pipeline',
    label: 'Code Translation',
    description: 'Translate COBOL logic to Java method implementations. Uses two independent translation approaches, validates both, and selects the best result.',
    icon: 'Languages',
    color: CATEGORY_COLORS.pipeline,
    apiEndpoint: '/codetranslation',
    apiMethod: 'POST',
    inputs: [
      { id: 'codeanalysis', name: 'Code Analysis', type: 'json', required: true },
    ],
    outputs: [
      { id: 'methods', name: 'Translated Methods', type: 'json' },
      { id: 'graph', name: 'Graph Updates', type: 'json' },
      { id: 'job_id', name: 'Job ID', type: 'string' },
    ],
    configSchema: [],
    defaultConfig: {},
  },
  'pipeline-smart-tests': {
    type: 'pipeline-smart-tests',
    disabled: true,
    category: 'pipeline',
    label: 'Smart Tests',
    description: 'Generate meaningful JUnit tests using AI with real assertions from COBOL logic',
    icon: 'Sparkles',
    color: CATEGORY_COLORS.pipeline,
    apiEndpoint: '/test-generation/smart',
    apiMethod: 'POST',
    inputs: [{ id: 'java', name: 'Java Code', type: 'json', required: true }],
    outputs: [
      { id: 'tests', name: 'Smart Tests', type: 'files' },
      { id: 'manifest', name: 'Test Manifest', type: 'json' },
    ],
    configSchema: [
      { key: 'source', label: 'Java Source', type: 'select', options: [
        { value: 'service_generation', label: 'Service Generation (Recommended - Real Services)' },
        { value: 'refactor', label: 'Code Refactor (Delegation Wrappers)' },
        { value: 'analysis', label: 'Code Analysis (Monolithic)' },
      ], default: 'service_generation', section: 'Source' },
      { key: 'framework', label: 'Test Framework', type: 'select', options: [
        { value: 'junit5', label: 'JUnit 5' },
        { value: 'testng', label: 'TestNG' },
      ], default: 'junit5', section: 'Framework' },
      { key: 'mock_framework', label: 'Mock Framework', type: 'select', options: [
        { value: 'mockito', label: 'Mockito' },
        { value: 'mockk', label: 'MockK' },
      ], default: 'mockito', section: 'Framework' },
      { key: 'unit_tests', label: 'Generate Unit Tests', type: 'boolean', default: true, section: 'Test Types' },
      { key: 'validation_tests', label: 'Generate Validation Tests (from Copybook)', type: 'boolean', default: true, section: 'Test Types' },
      { key: 'integration_tests', label: 'Generate Integration Tests', type: 'boolean', default: false, section: 'Test Types' },
      { key: 'coverage_target', label: 'Coverage Target', type: 'select', options: [
        { value: '60', label: '60% (Basic)' },
        { value: '80', label: '80% (Standard)' },
        { value: '100', label: '100% (Full)' },
      ], default: '80', section: 'Options' },
      { key: 'max_ai_calls', label: 'Max AI Calls (Cost Control)', type: 'number', default: 50, section: 'Options' },
    ],
    defaultConfig: { source: 'service_generation', framework: 'junit5', mock_framework: 'mockito', unit_tests: true, validation_tests: true, integration_tests: false, coverage_target: '80', max_ai_calls: 50 },
  },
  'pipeline-service-gen': {
    type: 'pipeline-service-gen',
    disabled: true,
    category: 'pipeline',
    label: 'Service Generation',
    description: 'Generate Spring Boot services from analyzed code. Preserves all business logic with full traceability back to COBOL.',
    icon: 'Component',
    color: CATEGORY_COLORS.pipeline,
    apiEndpoint: '/servicegeneration',
    apiMethod: 'POST',
    inputs: [
      { id: 'codeanalysis', name: 'Code Analysis', type: 'json', required: true },
      { id: 'coderefactor', name: 'Code Refactor', type: 'json', required: false },
      { id: 'dataanalysis', name: 'Data Analysis', type: 'json', required: false },
    ],
    outputs: [
      { id: 'services', name: 'Generated Services', type: 'files' },
      { id: 'traceability', name: 'Traceability Map', type: 'json' },
      { id: 'job_id', name: 'Job ID', type: 'string' },
    ],
    configSchema: [
      { key: 'program_name', label: 'Program Name', type: 'text', placeholder: 'IFPR321 (auto-detected from analysis)', section: 'General' },
      { key: 'base_package', label: 'Base Package', type: 'text', default: 'com.modernizeit.generated', section: 'General' },
      { key: 'include_all_repositories', label: 'Include All Repositories', type: 'boolean', default: true, section: 'Dependencies', description: 'Inject all ERD repositories regardless of detected usage' },
      { key: 'include_all_fields', label: 'Include All Fields', type: 'boolean', default: true, section: 'Dependencies', description: 'Include all fields from monolith regardless of detected usage' },
      { key: 'strict_validation', label: 'Strict Validation', type: 'boolean', default: true, section: 'Validation', description: 'Fail if any method body is empty or contains TODO stubs' },
    ],
    defaultConfig: { base_package: 'com.modernizeit.generated', include_all_repositories: true, include_all_fields: true, strict_validation: true },
  },
  'pipeline-java-analysis': {
    type: 'pipeline-java-analysis',
    disabled: true,
    category: 'pipeline',
    label: 'Java Analysis',
    description: 'Analyze generated Java for quality metrics — cohesion, coupling, complexity, and risk. Identifies cross-references between methods and fields, and flags areas needing improvement.',
    icon: 'FileSearch',
    color: CATEGORY_COLORS.pipeline,
    apiEndpoint: '/javaanalysis',
    apiMethod: 'POST',
    inputs: [
      { id: 'servicegeneration', name: 'Service Generation', type: 'json', required: true },
    ],
    outputs: [
      { id: 'analysis', name: 'Java Analysis Results', type: 'json' },
      { id: 'graph', name: 'Target Graph Nodes', type: 'json' },
      { id: 'job_id', name: 'Job ID', type: 'string' },
    ],
    configSchema: [
      { key: 'language', label: 'Target Language', type: 'select', options: [
        { value: 'java', label: 'Java' },
      ], default: 'java', section: 'General' },
    ],
    defaultConfig: { language: 'java' },
  },
  'pipeline-code-refactor-v2': {
    type: 'pipeline-code-refactor-v2',
    disabled: true,
    category: 'pipeline',
    label: 'Code Refactor v2',
    description: 'Apply automated fixes to Java quality findings — naming conventions, dead code, unused imports, and structural improvements. Validates compilation after each change.',
    icon: 'Wrench',
    color: CATEGORY_COLORS.pipeline,
    apiEndpoint: '/coderefactorv2',
    apiMethod: 'POST',
    inputs: [
      { id: 'javaanalysis', name: 'Java Analysis', type: 'json', required: true },
      { id: 'servicegeneration', name: 'Service Generation', type: 'json', required: true },
    ],
    outputs: [
      { id: 'refactoring', name: 'Refactoring Results', type: 'json' },
      { id: 'refactored_java', name: 'Refactored Java Source', type: 'json' },
      { id: 'job_id', name: 'Job ID', type: 'string' },
    ],
    configSchema: [],
    defaultConfig: {},
  },
  'pipeline-java-validation': {
    type: 'pipeline-java-validation',
    disabled: true,
    category: 'pipeline',
    label: 'Java Validation',
    description: 'Run mechanical repair and structural validation on generated Java before packaging. Reports downloadable even on failure.',
    icon: 'ShieldCheck',
    color: CATEGORY_COLORS.pipeline,
    apiEndpoint: '/java-validation/start',
    apiMethod: 'POST',
    inputs: [{ id: 'services', name: 'Generated Services', type: 'json', required: true }],
    outputs: [
      { id: 'validation', name: 'Validation Report', type: 'json' },
      { id: 'repair', name: 'Repair Report', type: 'json' },
      { id: 'validated_java', name: 'Validated Java', type: 'json' },
    ],
    configSchema: [
      { key: 'strict_compile', label: 'Strict Validation', type: 'boolean', default: false, section: 'Options', description: 'Promote warnings to errors' },
    ],
    defaultConfig: { strict_compile: false },
  },
  'pipeline-pre-flight': {
    type: 'pipeline-pre-flight',
    disabled: true,
    category: 'pipeline',
    label: 'Pre-Flight',
    description: 'Compile-check Java code before packaging. Catches errors early and produces downloadable reports on failure so you can review issues.',
    icon: 'Zap',
    color: CATEGORY_COLORS.pipeline,
    apiEndpoint: '/pre-flight/start',
    apiMethod: 'POST',
    inputs: [{ id: 'java', name: 'Java Source', type: 'json', required: true }],
    outputs: [
      { id: 'compile_result', name: 'Compile Result', type: 'json' },
    ],
    configSchema: [
      { key: 'java_version', label: 'Java Version', type: 'select', options: [
        { value: '21', label: 'Java 21 (LTS)' },
        { value: '17', label: 'Java 17 (LTS)' },
        { value: '11', label: 'Java 11 (LTS)' },
      ], default: '17', section: 'Framework' },
      { key: 'spring_boot_version', label: 'Spring Boot Version', type: 'select', options: [
        { value: '3.2.0', label: '3.2.0 (Latest)' },
        { value: '3.1.0', label: '3.1.0' },
        { value: '2.7.18', label: '2.7.18 (LTS)' },
      ], default: '3.2.0', section: 'Framework' },
    ],
    defaultConfig: { java_version: '17', spring_boot_version: '3.2.0' },
  },
  'pipeline-java-package': {
    type: 'pipeline-java-package',
    disabled: true,
    category: 'pipeline',
    label: 'Java Package',
    description: 'Package Java code into a production-ready Spring Boot application with Docker, tests, and build scripts',
    icon: 'Package',
    color: CATEGORY_COLORS.pipeline,
    apiEndpoint: '/java-packaging/start',
    apiMethod: 'POST',
    inputs: [{ id: 'refactor', name: 'Refactored Code', type: 'json', required: true }],
    outputs: [
      { id: 'package', name: 'Spring Boot Package', type: 'file' },
      { id: 'validation', name: 'Validation Report', type: 'json' },
    ],
    configSchema: [
      { key: 'source', label: 'Java Source', type: 'select', options: [
        { value: 'service_generation', label: 'Service Generation (Recommended - Real Services)' },
        { value: 'refactor', label: 'Code Refactor (Delegation Wrappers)' },
        { value: 'analysis', label: 'Code Analysis (Monolithic)' },
      ], default: 'service_generation', section: 'Source' },
      { key: 'spring_boot_version', label: 'Spring Boot Version', type: 'select', options: [
        { value: '3.2.0', label: '3.2.0 (Latest)' },
        { value: '3.1.0', label: '3.1.0' },
        { value: '2.7.18', label: '2.7.18 (LTS)' },
      ], default: '3.2.0', section: 'Framework' },
      { key: 'java_version', label: 'Java Version', type: 'select', options: [
        { value: '21', label: 'Java 21 (LTS)' },
        { value: '17', label: 'Java 17 (LTS)' },
        { value: '11', label: 'Java 11 (LTS)' },
      ], default: '17', section: 'Framework' },
      { key: 'include_docker', label: 'Include Docker Files', type: 'boolean', default: true, section: 'Options' },
      { key: 'include_tests', label: 'Include Test Stubs', type: 'boolean', default: true, section: 'Options' },
      { key: 'run_validation', label: 'Run Validation', type: 'boolean', default: true, section: 'Options' },
    ],
    defaultConfig: { source: 'service_generation', spring_boot_version: '3.2.0', java_version: '17', include_docker: true, include_tests: true, run_validation: true },
  },

  // ===========================================================================
  // CONTROL - Workflow control and utility nodes
  // ===========================================================================
  'wait': {
    type: 'wait',
    disabled: true,
    category: 'control',
    label: 'Wait',
    description: 'Pause workflow execution for a specified duration',
    icon: 'Clock',
    color: CATEGORY_COLORS.control,
    execution: 'local',
    inputs: [{ id: 'trigger', name: 'Trigger', type: 'any', required: true }],
    outputs: [{ id: 'continue', name: 'Continue', type: 'any' }],
    configSchema: [
      { key: 'duration', label: 'Duration', type: 'number', default: 5, section: 'General' },
      { key: 'unit', label: 'Unit', type: 'select', options: [
        { value: 'seconds', label: 'Seconds' },
        { value: 'minutes', label: 'Minutes' },
        { value: 'hours', label: 'Hours' },
      ], default: 'seconds', section: 'General' },
      { key: 'reason', label: 'Wait Reason', type: 'text', placeholder: 'Optional note', section: 'General' },
    ],
    defaultConfig: { duration: 5, unit: 'seconds', reason: '' },
  },
  'condition': {
    type: 'condition',
    disabled: true,
    category: 'control',
    label: 'Condition',
    description: 'Branch workflow based on a condition',
    icon: 'GitBranch',
    color: CATEGORY_COLORS.control,
    execution: 'local',
    inputs: [{ id: 'data', name: 'Input Data', type: 'any', required: true }],
    outputs: [
      { id: 'true', name: 'True Branch', type: 'any' },
      { id: 'false', name: 'False Branch', type: 'any' },
    ],
    configSchema: [
      { key: 'field', label: 'Field to Check', type: 'text', placeholder: 'e.g., status, metrics.complexity', section: 'Condition' },
      { key: 'operator', label: 'Operator', type: 'select', options: [
        { value: 'eq', label: 'Equals (==)' },
        { value: 'neq', label: 'Not Equals (!=)' },
        { value: 'gt', label: 'Greater Than (>)' },
        { value: 'gte', label: 'Greater or Equal (>=)' },
        { value: 'lt', label: 'Less Than (<)' },
        { value: 'lte', label: 'Less or Equal (<=)' },
        { value: 'contains', label: 'Contains' },
        { value: 'exists', label: 'Exists' },
      ], default: 'eq', section: 'Condition' },
      { key: 'value', label: 'Compare Value', type: 'text', section: 'Condition' },
    ],
    defaultConfig: { field: '', operator: 'eq', value: '' },
  },
  'loop': {
    type: 'loop',
    disabled: true,
    category: 'control',
    label: 'Loop',
    description: 'Iterate over a collection of items',
    icon: 'Repeat',
    color: CATEGORY_COLORS.control,
    execution: 'local',
    inputs: [{ id: 'items', name: 'Items', type: 'array', required: true }],
    outputs: [
      { id: 'item', name: 'Current Item', type: 'any' },
      { id: 'index', name: 'Index', type: 'number' },
      { id: 'done', name: 'Loop Complete', type: 'any' },
    ],
    configSchema: [
      { key: 'maxIterations', label: 'Max Iterations', type: 'number', default: 100, section: 'Limits' },
      { key: 'parallel', label: 'Run in Parallel', type: 'boolean', default: false, section: 'Options' },
      { key: 'batchSize', label: 'Batch Size', type: 'number', default: 5, section: 'Options' },
    ],
    defaultConfig: { maxIterations: 100, parallel: false, batchSize: 5 },
  },
  'parallel': {
    type: 'parallel',
    disabled: true,
    category: 'control',
    label: 'Parallel',
    description: 'Execute multiple branches simultaneously',
    icon: 'GitMerge',
    color: CATEGORY_COLORS.control,
    execution: 'local',
    inputs: [{ id: 'trigger', name: 'Trigger', type: 'any', required: true }],
    outputs: [
      { id: 'branch1', name: 'Branch 1', type: 'any' },
      { id: 'branch2', name: 'Branch 2', type: 'any' },
      { id: 'branch3', name: 'Branch 3', type: 'any' },
    ],
    configSchema: [
      { key: 'branches', label: 'Number of Branches', type: 'number', default: 2, section: 'General' },
      { key: 'waitForAll', label: 'Wait for All to Complete', type: 'boolean', default: true, section: 'Options' },
    ],
    defaultConfig: { branches: 2, waitForAll: true },
  },
  'checkpoint': {
    type: 'checkpoint',
    disabled: true,
    category: 'control',
    label: 'Checkpoint',
    description: 'Save workflow state for resume capability',
    icon: 'Save',
    color: CATEGORY_COLORS.control,
    execution: 'local',
    inputs: [{ id: 'data', name: 'Data to Save', type: 'any', required: true }],
    outputs: [{ id: 'continue', name: 'Continue', type: 'any' }],
    configSchema: [
      { key: 'name', label: 'Checkpoint Name', type: 'text', default: 'checkpoint', section: 'General' },
      { key: 'description', label: 'Description', type: 'text', section: 'General' },
    ],
    defaultConfig: { name: 'checkpoint', description: '' },
  },
  'notify': {
    type: 'notify',
    disabled: true,
    category: 'control',
    label: 'Notify',
    description: 'Send notification via email, Slack, or webhook',
    icon: 'Bell',
    color: CATEGORY_COLORS.control,
    execution: 'local',
    inputs: [
      { id: 'trigger', name: 'Trigger', type: 'any', required: true },
      { id: 'data', name: 'Data', type: 'any' },
    ],
    outputs: [{ id: 'sent', name: 'Sent', type: 'boolean' }],
    configSchema: [
      { key: 'channel', label: 'Channel', type: 'select', options: [
        { value: 'email', label: 'Email' },
        { value: 'slack', label: 'Slack' },
        { value: 'webhook', label: 'Webhook' },
      ], default: 'email', section: 'Channel' },
      { key: 'recipient', label: 'Recipient', type: 'text', placeholder: 'Email, channel, or URL', section: 'Channel' },
      { key: 'subject', label: 'Subject', type: 'text', default: 'Workflow Notification', section: 'Message' },
      { key: 'message', label: 'Message', type: 'textarea', default: 'Workflow step completed.', section: 'Message' },
    ],
    defaultConfig: { channel: 'email', recipient: '', subject: 'Workflow Notification', message: 'Workflow step completed.' },
  },
  'custom-script': {
    type: 'custom-script',
    disabled: true,
    category: 'control',
    label: 'Custom Script',
    description: 'Run custom Python or JavaScript code',
    icon: 'Code2',
    color: CATEGORY_COLORS.control,
    execution: 'sandbox',
    inputs: [{ id: 'data', name: 'Input Data', type: 'any' }],
    outputs: [
      { id: 'result', name: 'Result', type: 'any' },
      { id: 'logs', name: 'Logs', type: 'string' },
    ],
    configSchema: [
      { key: 'language', label: 'Language', type: 'select', options: [
        { value: 'python', label: 'Python 3' },
        { value: 'javascript', label: 'JavaScript' },
      ], default: 'python', section: 'General' },
      { key: 'code', label: 'Code', type: 'textarea', default: '# Input: input_data\n# Output: result\nresult = input_data', section: 'General' },
      { key: 'timeout', label: 'Timeout (sec)', type: 'number', default: 30, section: 'Advanced' },
    ],
    defaultConfig: { language: 'python', code: '# Input: input_data\n# Output: result\nresult = input_data', timeout: 30 },
  },

  // ===========================================================================
  // INGEST - Load source input, normalize, stage files
  // ===========================================================================
  'upload-files': {
    type: 'upload-files',
    disabled: true,
    category: 'ingest',
    label: 'Upload Files',
    description: 'Upload ZIP archive or folder of source files',
    icon: 'Upload',
    color: CATEGORY_COLORS.ingest,
    apiEndpoint: '/ingest/upload',
    apiMethod: 'POST',
    inputs: [],
    outputs: [
      { id: 'files', name: 'Source Files', type: 'files' },
      { id: 'source_hash', name: 'Source Hash', type: 'string' },
      { id: 'job_id', name: 'Job ID', type: 'string' },
    ],
    configSchema: [
      { key: 'accept', label: 'File Types', type: 'text', default: '.zip,.tar,.gz', section: 'General' },
      { key: 'sourceLanguage', label: 'Source Language', type: 'select', options: [
        { value: 'cobol', label: 'COBOL' },
        { value: 'pli', label: 'PL/I' },
        { value: 'natural', label: 'Natural' },
        { value: 'rpg', label: 'RPG' },
      ], default: 'cobol', section: 'Languages' },
      { key: 'targetLanguage', label: 'Target Language', type: 'select', options: [
        { value: 'java', label: 'Java' },
        { value: 'csharp', label: 'C#' },
        { value: 'python', label: 'Python' },
        { value: 'typescript', label: 'TypeScript' },
      ], default: 'java', section: 'Languages' },
      { key: 'generateTypeMappings', label: 'Generate Type Mappings', type: 'boolean', default: true, section: 'Options' },
      { key: 'autoExtract', label: 'Auto-extract Archives', type: 'boolean', default: true, section: 'Options' },
      { key: 'encoding', label: 'File Encoding', type: 'select', options: [
        { value: 'auto', label: 'Auto-detect' },
        { value: 'utf8', label: 'UTF-8' },
        { value: 'ebcdic', label: 'EBCDIC' },
        { value: 'ascii', label: 'ASCII' },
      ], default: 'auto', section: 'Options' },
      { key: 'includePatterns', label: 'Include Patterns', type: 'text', placeholder: '*.cbl, *.cpy, *.pli', section: 'Filtering' },
      { key: 'excludePatterns', label: 'Exclude Patterns', type: 'text', placeholder: '*.bak, *.tmp', section: 'Filtering' },
    ],
    defaultConfig: { accept: '.zip,.tar,.gz', sourceLanguage: 'cobol', targetLanguage: 'java', generateTypeMappings: true, autoExtract: true, encoding: 'auto', includePatterns: '', excludePatterns: '' },
  },
  'upload-single': {
    type: 'upload-single',
    disabled: true,
    category: 'ingest',
    label: 'Upload Single File',
    description: 'Upload a single source file for quick analysis',
    icon: 'FileUp',
    color: CATEGORY_COLORS.ingest,
    inputs: [],
    outputs: [{ id: 'file', name: 'Source File', type: 'file' }],
    configSchema: [
      { key: 'accept', label: 'File Types', type: 'text', default: '*', section: 'General' },
    ],
    defaultConfig: { accept: '*' },
  },
  'git-import': {
    type: 'git-import',
    disabled: true,
    category: 'ingest',
    label: 'Git Import',
    description: 'Clone repository and import source files',
    icon: 'GitBranch',
    color: CATEGORY_COLORS.ingest,
    disabled: true,  // Future: Git integration
    inputs: [],
    outputs: [{ id: 'files', name: 'Source Files', type: 'files' }],
    configSchema: [
      { key: 'url', label: 'Repository URL', type: 'text', required: true, section: 'Repository' },
      { key: 'branch', label: 'Branch', type: 'text', default: 'main', section: 'Repository' },
      { key: 'path', label: 'Subdirectory Path', type: 'text', default: '', section: 'Repository' },
      { key: 'depth', label: 'Clone Depth', type: 'number', default: 1, section: 'Options' },
    ],
    defaultConfig: { url: '', branch: 'main', path: '', depth: 1 },
  },

  // ===========================================================================
  // PARSE - Parse source, extract structure, prepare for analysis
  // NOTE: Disabled - handled by pipeline-code-analysis
  // ===========================================================================
  'parse-source': {
    type: 'parse-source',
    disabled: true,
    category: 'parse',
    label: 'Parse Source',
    description: 'Parse source files and build AST (handled by Code Analysis)',
    icon: 'FileCode',
    color: CATEGORY_COLORS.parse,
    disabled: true,  // Handled by pipeline-code-analysis
    inputs: [{ id: 'files', name: 'Source Files', type: 'files', required: true }],
    outputs: [{ id: 'ast', name: 'Parsed AST', type: 'ast' }],
    configSchema: [
      { key: 'language', label: 'Source Language', type: 'select', options: [
        { value: 'cobol', label: 'COBOL' },
        { value: 'pli', label: 'PL/I' },
        { value: 'natural', label: 'Natural' },
        { value: 'rpg', label: 'RPG' },
        { value: 'java', label: 'Java' },
        { value: 'csharp', label: 'C#' },
        { value: 'auto', label: 'Auto-detect' },
      ], default: 'cobol', section: 'General' },
      { key: 'dialect', label: 'Dialect/Version', type: 'text', default: '', section: 'General' },
      { key: 'encoding', label: 'File Encoding', type: 'select', options: [
        { value: 'utf8', label: 'UTF-8' },
        { value: 'ebcdic', label: 'EBCDIC' },
        { value: 'ascii', label: 'ASCII' },
        { value: 'auto', label: 'Auto-detect' },
      ], default: 'auto', section: 'General' },
      { key: 'strict', label: 'Strict Parsing', type: 'boolean', default: false, section: 'Options' },
    ],
    defaultConfig: { language: 'cobol', dialect: '', encoding: 'auto', strict: false },
  },
  'resolve-includes': {
    type: 'resolve-includes',
    disabled: true,
    category: 'parse',
    label: 'Resolve Includes',
    description: 'Resolve and inline included files (handled by Code Analysis)',
    icon: 'FileSymlink',
    color: CATEGORY_COLORS.parse,
    disabled: true,  // Handled by pipeline-code-analysis
    inputs: [{ id: 'ast', name: 'Parsed AST', type: 'ast', required: true }],
    outputs: [{ id: 'ast', name: 'Resolved AST', type: 'ast' }],
    configSchema: [
      { key: 'searchPaths', label: 'Include Search Paths', type: 'textarea', default: '', section: 'General' },
      { key: 'onMissing', label: 'Missing Include Handling', type: 'select', options: [
        { value: 'error', label: 'Fail with Error' },
        { value: 'warn', label: 'Warn and Continue' },
        { value: 'skip', label: 'Skip Silently' },
      ], default: 'warn', section: 'General' },
    ],
    defaultConfig: { searchPaths: '', onMissing: 'warn' },
  },
  'build-symbols': {
    type: 'build-symbols',
    disabled: true,
    category: 'parse',
    label: 'Build Symbol Table',
    description: 'Index all symbols, variables, and references (handled by Code Analysis)',
    icon: 'Database',
    color: CATEGORY_COLORS.parse,
    disabled: true,  // Handled by pipeline-code-analysis
    inputs: [{ id: 'ast', name: 'Parsed AST', type: 'ast', required: true }],
    outputs: [{ id: 'symbols', name: 'Symbol Table', type: 'symbols' }],
    configSchema: [
      { key: 'includeUnused', label: 'Include Unused Symbols', type: 'boolean', default: true, section: 'Options' },
      { key: 'resolveTypes', label: 'Resolve Type References', type: 'boolean', default: true, section: 'Options' },
    ],
    defaultConfig: { includeUnused: true, resolveTypes: true },
  },

  // ===========================================================================
  // ANALYZE - Code Analysis: structure, control flow, complexity
  // ===========================================================================
  'code-analysis': {
    type: 'code-analysis',
    disabled: true,
    category: 'analyze',
    label: 'Code Analysis',
    description: 'Parse source code and generate target language',
    icon: 'FileCode',
    color: CATEGORY_COLORS.analyze,
    apiEndpoint: '/codeanalysis',
    apiMethod: 'POST',
    inputs: [{ id: 'files', name: 'Source Files', type: 'files', required: true }],
    outputs: [
      { id: 'analysis', name: 'Analysis Results', type: 'json' },
      { id: 'generated', name: 'Generated Code', type: 'files' },
    ],
    configSchema: [
      { key: 'main_program', label: 'Main Program', type: 'text', placeholder: 'Auto-detect', section: 'General' },
      { key: 'source_lang', label: 'Source Language', type: 'select', options: [
        { value: 'cobol', label: 'COBOL' },
        { value: 'pli', label: 'PL/I' },
        { value: 'natural', label: 'Natural' },
        { value: 'rpg', label: 'RPG' },
      ], default: 'cobol', section: 'Languages' },
      { key: 'target_lang', label: 'Target Language', type: 'select', options: [
        { value: 'java', label: 'Java' },
        { value: 'csharp', label: 'C#' },
        { value: 'python', label: 'Python' },
        { value: 'typescript', label: 'TypeScript' },
      ], default: 'java', section: 'Languages' },
      { key: 'generate_java', label: 'Generate Java Code', type: 'boolean', default: true, section: 'Options' },
      { key: 'generate_graphs', label: 'Generate Call Graphs', type: 'boolean', default: true, section: 'Options' },
      { key: 'generate_inventory', label: 'Generate Line Inventory', type: 'boolean', default: true, section: 'Options' },
    ],
    defaultConfig: { source_lang: 'cobol', target_lang: 'java', generate_java: true, generate_graphs: true, generate_inventory: true },
  },
  'java-analysis': {
    type: 'java-analysis',
    disabled: true,
    category: 'analyze',
    label: 'Java Analysis',
    description: 'Analyze generated Java for quality metrics — cohesion, coupling, complexity, and risk. Identifies cross-references between methods and fields, and flags areas needing improvement.',
    icon: 'FileSearch',
    color: CATEGORY_COLORS.analyze,
    apiEndpoint: '/javaanalysis',
    apiMethod: 'POST',
    inputs: [
      { id: 'servicegeneration', name: 'Service Generation', type: 'json', required: true },
    ],
    outputs: [
      { id: 'analysis', name: 'Java Analysis Results', type: 'json' },
      { id: 'graph', name: 'Target Graph Nodes', type: 'json' },
      { id: 'job_id', name: 'Job ID', type: 'string' },
    ],
    configSchema: [
      { key: 'language', label: 'Target Language', type: 'select', options: [
        { value: 'java', label: 'Java' },
      ], default: 'java', section: 'General' },
    ],
    defaultConfig: { language: 'java' },
  },
  'idms-analysis': {
    type: 'idms-analysis',
    disabled: true,
    category: 'analyze',
    label: 'IDMS Analysis',
    description: 'Scan COBOL programs for IDMS database usage — identifies data operations, schema inventory, copybook layouts, and program structure to support migration planning.',
    icon: 'Database',
    color: CATEGORY_COLORS.analyze,
    apiEndpoint: '/idmsanalysis',
    apiMethod: 'POST',
    inputs: [{ id: 'files', name: 'Source Files', type: 'files', required: true }],
    outputs: [
      { id: 'analysis', name: 'IDMS Analysis Results', type: 'json' },
      { id: 'graph', name: 'IDMS Graph Nodes', type: 'json' },
      { id: 'job_id', name: 'Job ID', type: 'string' },
    ],
    configSchema: [],
    defaultConfig: {},
  },
  'idms-compatibility': {
    type: 'idms-compatibility',
    disabled: true,
    category: 'analyze',
    label: 'IDMS Compatibility',
    description: 'Check COBOL V3-to-V6 compatibility. Scans for CMPR2 breaking changes (8 rules), applies automated fixes, generates V6 COBOL output with change reports and JCL parameter updates.',
    icon: 'ShieldCheck',
    color: CATEGORY_COLORS.analyze,
    apiEndpoint: '/idmscompatibility',
    apiMethod: 'POST',
    inputs: [{ id: 'analysis', name: 'IDMS Analysis Results', type: 'json', required: true }],
    outputs: [
      { id: 'findings', name: 'CMPR2 Findings', type: 'json' },
      { id: 'v6_output', name: 'V6 COBOL Output', type: 'files' },
      { id: 'job_id', name: 'Job ID', type: 'string' },
    ],
    configSchema: [],
    defaultConfig: {},
  },
  'idms-data-architecture': {
    type: 'idms-data-architecture',
    disabled: true,
    category: 'analyze',
    label: 'IDMS Data Architecture',
    description: 'Map out the database schema and record relationships from how programs access data. Produces a visual relationship diagram for migration planning.',
    icon: 'GitBranch',
    color: CATEGORY_COLORS.analyze,
    apiEndpoint: '/idmsdataarchitecture',
    apiMethod: 'POST',
    inputs: [{ id: 'analysis', name: 'IDMS Analysis Results', type: 'json', required: true }],
    outputs: [
      { id: 'schema', name: 'Inferred Schema', type: 'json' },
      { id: 'graph', name: 'Data Architecture Graph', type: 'json' },
      { id: 'job_id', name: 'Job ID', type: 'string' },
    ],
    configSchema: [],
    defaultConfig: {},
  },
  'idms-assessment': {
    type: 'idms-assessment',
    disabled: true,
    category: 'analyze',
    label: 'IDMS Assessment',
    description: 'Score migration readiness per program. Evaluates data complexity, code complexity, coupling, and compatibility to produce effort estimates and a recommended migration sequence.',
    icon: 'ClipboardCheck',
    color: CATEGORY_COLORS.analyze,
    apiEndpoint: '/idmsassessment',
    apiMethod: 'POST',
    inputs: [
      { id: 'analysis', name: 'IDMS Analysis Results', type: 'json', required: true },
      { id: 'compatibility', name: 'IDMS Compatibility Results', type: 'json', required: false },
    ],
    outputs: [
      { id: 'assessment', name: 'Assessment Summary', type: 'json' },
      { id: 'risks', name: 'Program Risk Scores', type: 'json' },
      { id: 'job_id', name: 'Job ID', type: 'string' },
    ],
    configSchema: [],
    defaultConfig: {},
  },
  'idms-sql-generation': {
    type: 'idms-sql-generation',
    disabled: true,
    category: 'analyze',
    label: 'IDMS SQL Generation',
    description: 'Generate table definitions and stored procedures for your target database. Supports SQL Server, DB2, and Oracle. Maps COBOL data types to SQL columns with proper keys and relationships.',
    icon: 'Database',
    color: CATEGORY_COLORS.analyze,
    apiEndpoint: '/idmssqlgeneration',
    apiMethod: 'POST',
    inputs: [
      { id: 'analysis', name: 'IDMS Analysis Results', type: 'json', required: true },
      { id: 'data_architecture', name: 'IDMS Data Architecture Results', type: 'json', required: true },
    ],
    outputs: [
      { id: 'ddl', name: 'Full DDL Script', type: 'text' },
      { id: 'dml', name: 'Stored Procedures Script', type: 'text' },
      { id: 'tables', name: 'Table Details', type: 'json' },
      { id: 'job_id', name: 'Job ID', type: 'string' },
    ],
    configSchema: [
      {
        key: 'dialect',
        label: 'SQL Dialect',
        type: 'select',
        options: ['SQL Server', 'DB2', 'Oracle'],
        default: 'SQL Server',
      },
    ],
    defaultConfig: { dialect: 'SQL Server' },
  },
  'business-logic': {
    type: 'business-logic',
    disabled: true,
    category: 'analyze',
    label: 'Business Logic',
    description: 'Analyze COBOL business logic semantics — ordered execution steps, data transformations, business rules, error handling. Enriches Paragraph graph nodes with business specs for Service Generation.',
    icon: 'BookOpen',
    color: CATEGORY_COLORS.analyze,
    apiEndpoint: '/businesslogic',
    apiMethod: 'POST',
    inputs: [
      { id: 'codeanalysis', name: 'Code Analysis', type: 'json', required: true },
    ],
    outputs: [
      { id: 'specs', name: 'Business Specs', type: 'json' },
      { id: 'graph', name: 'Graph Updates', type: 'json' },
      { id: 'job_id', name: 'Job ID', type: 'string' },
    ],
    configSchema: [],
    defaultConfig: {},
  },
  'code-translation': {
    type: 'code-translation',
    disabled: true,
    category: 'generate',
    label: 'Code Translation',
    description: 'Translate COBOL logic to Java method implementations. Uses two independent translation approaches, validates both, and selects the best result.',
    icon: 'Languages',
    color: CATEGORY_COLORS.generate,
    apiEndpoint: '/codetranslation',
    apiMethod: 'POST',
    inputs: [
      { id: 'codeanalysis', name: 'Code Analysis', type: 'json', required: true },
    ],
    outputs: [
      { id: 'methods', name: 'Translated Methods', type: 'json' },
      { id: 'graph', name: 'Graph Updates', type: 'json' },
      { id: 'job_id', name: 'Job ID', type: 'string' },
    ],
    configSchema: [],
    defaultConfig: {},
  },
  'structure-analysis': {
    type: 'structure-analysis',
    disabled: true,
    category: 'analyze',
    label: 'Structure Analysis',
    description: 'Analyze code structure: divisions, sections, functions, classes',
    icon: 'Layers',
    color: CATEGORY_COLORS.analyze,
    disabled: true,  // Future: granular structure analysis
    inputs: [{ id: 'ast', name: 'Parsed AST', type: 'ast', required: true }],
    outputs: [{ id: 'structure', name: 'Structure Report', type: 'json' }],
    configSchema: [
      { key: 'depth', label: 'Analysis Depth', type: 'select', options: [
        { value: 'shallow', label: 'Shallow (top-level only)' },
        { value: 'medium', label: 'Medium' },
        { value: 'deep', label: 'Deep (full hierarchy)' },
      ], default: 'deep', section: 'General' },
    ],
    defaultConfig: { depth: 'deep' },
  },
  'control-flow': {
    type: 'control-flow',
    disabled: true,
    category: 'analyze',
    label: 'Control Flow Analysis',
    description: 'Analyze control flow: PERFORM, CALL, GO TO, branches',
    icon: 'GitFork',
    color: CATEGORY_COLORS.analyze,
    disabled: true,  // Future: dedicated control flow analysis
    inputs: [{ id: 'ast', name: 'Parsed AST', type: 'ast', required: true }],
    outputs: [{ id: 'controlFlow', name: 'Control Flow Graph', type: 'graph' }],
    configSchema: [
      { key: 'includeExternal', label: 'Include External Calls', type: 'boolean', default: true, section: 'Options' },
      { key: 'maxDepth', label: 'Max Call Depth', type: 'number', default: 20, section: 'Options' },
    ],
    defaultConfig: { includeExternal: true, maxDepth: 20 },
  },
  'variable-analysis': {
    type: 'variable-analysis',
    disabled: true,
    category: 'analyze',
    label: 'Variable Analysis',
    description: 'Analyze variables, fields, and data usage',
    icon: 'Variable',
    color: CATEGORY_COLORS.analyze,
    disabled: true,  // Future: dedicated variable tracking
    inputs: [
      { id: 'ast', name: 'Parsed AST', type: 'ast', required: true },
      { id: 'symbols', name: 'Symbol Table', type: 'symbols' },
    ],
    outputs: [{ id: 'variables', name: 'Variable Report', type: 'json' }],
    configSchema: [
      { key: 'trackUsage', label: 'Track Read/Write Usage', type: 'boolean', default: true, section: 'Options' },
      { key: 'detectUnused', label: 'Detect Unused Variables', type: 'boolean', default: true, section: 'Options' },
    ],
    defaultConfig: { trackUsage: true, detectUnused: true },
  },
  'sql-analysis': {
    type: 'sql-analysis',
    disabled: true,
    category: 'analyze',
    label: 'SQL/Database Analysis',
    description: 'Extract and analyze embedded SQL and database operations',
    icon: 'Database',
    color: CATEGORY_COLORS.analyze,
    disabled: true,  // Future: dedicated SQL extraction
    inputs: [{ id: 'ast', name: 'Parsed AST', type: 'ast', required: true }],
    outputs: [{ id: 'sql', name: 'SQL Analysis', type: 'json' }],
    configSchema: [
      { key: 'dbType', label: 'Database Type', type: 'select', options: [
        { value: 'db2', label: 'DB2' },
        { value: 'oracle', label: 'Oracle' },
        { value: 'sqlserver', label: 'SQL Server' },
        { value: 'auto', label: 'Auto-detect' },
      ], default: 'auto', section: 'General' },
      { key: 'extractDDL', label: 'Extract DDL Statements', type: 'boolean', default: true, section: 'Options' },
    ],
    defaultConfig: { dbType: 'auto', extractDDL: true },
  },
  'complexity-metrics': {
    type: 'complexity-metrics',
    disabled: true,
    category: 'analyze',
    label: 'Complexity Metrics',
    description: 'Calculate complexity: McCabe, Halstead, LOC, maintainability',
    icon: 'BarChart3',
    color: CATEGORY_COLORS.analyze,
    disabled: true,  // Future: dedicated metrics calculation
    inputs: [{ id: 'ast', name: 'Parsed AST', type: 'ast', required: true }],
    outputs: [{ id: 'metrics', name: 'Metrics Report', type: 'json' }],
    configSchema: [
      { key: 'threshold', label: 'Complexity Threshold', type: 'number', default: 10, section: 'Thresholds' },
      { key: 'includeHalstead', label: 'Include Halstead Metrics', type: 'boolean', default: true, section: 'Options' },
    ],
    defaultConfig: { threshold: 10, includeHalstead: true },
  },

  // ===========================================================================
  // REFACTOR - Code Refactoring: patterns, simplification, standardization
  // ===========================================================================
  'code-refactor': {
    type: 'code-refactor',
    disabled: true,
    category: 'refactor',
    label: 'Code Refactor',
    description: 'Analyze generated Java code for refactoring opportunities. Detects patterns, suggests class splitting, and can transform code.',
    icon: 'Wand2',
    color: CATEGORY_COLORS.refactor,
    apiEndpoint: '/coderefactor',
    apiMethod: 'POST',
    inputs: [{ id: 'analysis', name: 'Code Analysis Results', type: 'json', required: true }],
    outputs: [
      { id: 'report', name: 'Refactor Report', type: 'json' },
      { id: 'recipes', name: 'Refactor Recipes', type: 'json' },
      { id: 'refactored', name: 'Refactored Code', type: 'files' },
    ],
    configSchema: [
      // General settings
      { key: 'class_name', label: 'Class Name (optional)', type: 'text', placeholder: 'All classes (leave empty)', section: 'General' },
      // AI settings
      { key: 'use_ai', label: 'Use AI Analysis (Claude)', type: 'boolean', default: true, section: 'Options' },
      // Transform - applies recipes and creates new Java files in separate location
      { key: 'apply_transformations', label: 'Apply Transformations', type: 'boolean', default: true, section: 'Options', description: 'Create refactored Java files (non-destructive)' },
      // Recipe filter (only relevant when apply_transformations is true)
      { key: 'recipes_to_apply', label: 'Recipes to Apply', type: 'select', options: [
        { value: 'all', label: 'All Recipes' },
        { value: 'high_confidence', label: 'High Confidence Only (>90%)' },
        { value: 'extract_class', label: 'Extract Class Only' },
      ], default: 'all', section: 'Options' },
    ],
    defaultConfig: { use_ai: true, apply_transformations: true, recipes_to_apply: 'all' },
  },
  'code-refactor-v2': {
    type: 'code-refactor-v2',
    disabled: true,
    category: 'refactor',
    label: 'Code Refactor v2',
    description: 'Apply automated fixes to Java quality findings — naming conventions, dead code, unused imports, and structural improvements. Validates compilation after each change.',
    icon: 'Wrench',
    color: CATEGORY_COLORS.refactor,
    apiEndpoint: '/coderefactorv2',
    apiMethod: 'POST',
    inputs: [
      { id: 'javaanalysis', name: 'Java Analysis', type: 'json', required: true },
      { id: 'servicegeneration', name: 'Service Generation', type: 'json', required: true },
    ],
    outputs: [
      { id: 'refactoring', name: 'Refactoring Results', type: 'json' },
      { id: 'refactored_java', name: 'Refactored Java Source', type: 'json' },
      { id: 'job_id', name: 'Job ID', type: 'string' },
    ],
    configSchema: [],
    defaultConfig: {},
  },
  'apply-patterns': {
    type: 'apply-patterns',
    disabled: true,
    category: 'refactor',
    label: 'Apply Patterns',
    description: 'Apply refactoring patterns and transformations',
    icon: 'Wand2',
    color: CATEGORY_COLORS.refactor,
    disabled: true,  // Future: granular pattern application
    inputs: [{ id: 'ast', name: 'Parsed AST', type: 'ast', required: true }],
    outputs: [{ id: 'ast', name: 'Refactored AST', type: 'ast' }],
    configSchema: [
      { key: 'patterns', label: 'Pattern Set', type: 'select', options: [
        { value: 'all', label: 'All Patterns' },
        { value: 'structure', label: 'Structure Patterns' },
        { value: 'control', label: 'Control Flow Patterns' },
        { value: 'custom', label: 'Custom Selection' },
      ], default: 'all', section: 'General' },
      { key: 'preview', label: 'Preview Only (no changes)', type: 'boolean', default: false, section: 'Options' },
    ],
    defaultConfig: { patterns: 'all', preview: false },
  },
  'simplify-code': {
    type: 'simplify-code',
    disabled: true,
    category: 'refactor',
    label: 'Simplify Code',
    description: 'Simplify conditions, remove dead code, flatten nesting',
    icon: 'Minimize2',
    color: CATEGORY_COLORS.refactor,
    disabled: true,  // Future: dedicated code simplification
    inputs: [{ id: 'ast', name: 'Parsed AST', type: 'ast', required: true }],
    outputs: [{ id: 'ast', name: 'Simplified AST', type: 'ast' }],
    configSchema: [
      { key: 'removeDeadCode', label: 'Remove Dead Code', type: 'boolean', default: true, section: 'Options' },
      { key: 'simplifyConditions', label: 'Simplify Conditions', type: 'boolean', default: true, section: 'Options' },
      { key: 'maxNestingDepth', label: 'Max Nesting Depth', type: 'number', default: 4, section: 'Options' },
    ],
    defaultConfig: { removeDeadCode: true, simplifyConditions: true, maxNestingDepth: 4 },
  },
  'standardize-code': {
    type: 'standardize-code',
    disabled: true,
    category: 'refactor',
    label: 'Standardize Code',
    description: 'Apply coding standards and naming conventions',
    icon: 'AlignLeft',
    color: CATEGORY_COLORS.refactor,
    disabled: true,  // Future: dedicated standardization
    inputs: [{ id: 'ast', name: 'Parsed AST', type: 'ast', required: true }],
    outputs: [{ id: 'ast', name: 'Standardized AST', type: 'ast' }],
    configSchema: [
      { key: 'namingConvention', label: 'Naming Convention', type: 'select', options: [
        { value: 'camelCase', label: 'camelCase' },
        { value: 'PascalCase', label: 'PascalCase' },
        { value: 'snake_case', label: 'snake_case' },
        { value: 'preserve', label: 'Preserve Original' },
      ], default: 'camelCase', section: 'General' },
      { key: 'formatCode', label: 'Format/Indent Code', type: 'boolean', default: true, section: 'Options' },
    ],
    defaultConfig: { namingConvention: 'camelCase', formatCode: true },
  },
  'extract-functions': {
    type: 'extract-functions',
    disabled: true,
    category: 'refactor',
    label: 'Extract Functions',
    description: 'Extract inline code into reusable functions/paragraphs',
    icon: 'PackagePlus',
    color: CATEGORY_COLORS.refactor,
    disabled: true,  // Future: dedicated function extraction
    inputs: [{ id: 'ast', name: 'Parsed AST', type: 'ast', required: true }],
    outputs: [{ id: 'ast', name: 'Refactored AST', type: 'ast' }],
    configSchema: [
      { key: 'minLines', label: 'Min Lines for Extraction', type: 'number', default: 10, section: 'Options' },
      { key: 'detectDuplication', label: 'Detect Duplicated Code', type: 'boolean', default: true, section: 'Options' },
    ],
    defaultConfig: { minLines: 10, detectDuplication: true },
  },

  // ===========================================================================
  // MAP - Dependency Mapper: call graphs, include maps, external systems
  // ===========================================================================
  'dependency-mapper': {
    type: 'dependency-mapper',
    disabled: true,
    category: 'map',
    label: 'Dependency Mapper',
    description: 'Build dependency graphs and analyze relationships',
    icon: 'GitFork',
    color: CATEGORY_COLORS.map,
    apiEndpoint: '/dependencymapper',
    apiMethod: 'POST',
    inputs: [{ id: 'analysis', name: 'Analysis Results', type: 'json', required: true }],
    outputs: [{ id: 'graph', name: 'Dependency Graph', type: 'graph' }],
    configSchema: [
      { key: 'analysis_type', label: 'Analysis Type', type: 'select', options: [
        { value: 'cobol', label: 'COBOL Dependencies' },
        { value: 'java', label: 'Java Dependencies' },
      ], default: 'cobol', section: 'General' },
      { key: 'include_externals', label: 'Include External Calls', type: 'boolean', default: true, section: 'Options' },
      { key: 'missing_symbol_default_action', label: 'Missing Symbols Default Action', type: 'select', options: [
        { value: 'compile-only-stub', label: 'Generate compile-only stubs (default)' },
        { value: 'fix-now', label: 'Fix now (open editor)' },
        { value: 'mark-external', label: 'Mark as external dependency' },
      ], default: 'compile-only-stub', section: 'Options' },
    ],
    defaultConfig: { analysis_type: 'cobol', include_externals: true, missing_symbol_default_action: 'compile-only-stub' },
  },
  'build-call-graph': {
    type: 'build-call-graph',
    disabled: true,
    category: 'map',
    label: 'Build Call Graph',
    description: 'Map program-to-program calls and invocations',
    icon: 'GitFork',
    color: CATEGORY_COLORS.map,
    disabled: true,  // Future: dedicated call graph builder
    inputs: [{ id: 'ast', name: 'Parsed AST', type: 'ast', required: true }],
    outputs: [{ id: 'callGraph', name: 'Call Graph', type: 'graph' }],
    configSchema: [
      { key: 'includeInternal', label: 'Include Internal Calls', type: 'boolean', default: true, section: 'Options' },
      { key: 'includeExternal', label: 'Include External Calls', type: 'boolean', default: true, section: 'Options' },
      { key: 'maxDepth', label: 'Max Depth', type: 'number', default: 10, section: 'Options' },
    ],
    defaultConfig: { includeInternal: true, includeExternal: true, maxDepth: 10 },
  },
  'map-includes': {
    type: 'map-includes',
    disabled: true,
    category: 'map',
    label: 'Map Includes',
    description: 'Map include/copybook dependencies between programs',
    icon: 'FileSymlink',
    color: CATEGORY_COLORS.map,
    disabled: true,  // Future: dedicated include mapper
    inputs: [{ id: 'ast', name: 'Parsed AST', type: 'ast', required: true }],
    outputs: [{ id: 'includeMap', name: 'Include Map', type: 'graph' }],
    configSchema: [
      { key: 'showVersions', label: 'Show Version Info', type: 'boolean', default: false, section: 'Options' },
    ],
    defaultConfig: { showVersions: false },
  },
  'map-data-deps': {
    type: 'map-data-deps',
    disabled: true,
    category: 'map',
    label: 'Map Data Dependencies',
    description: 'Map data flow between programs and files',
    icon: 'ArrowRightLeft',
    color: CATEGORY_COLORS.map,
    disabled: true,  // Future: dedicated data dependency mapper
    inputs: [
      { id: 'ast', name: 'Parsed AST', type: 'ast', required: true },
      { id: 'sql', name: 'SQL Analysis', type: 'json' },
    ],
    outputs: [{ id: 'dataMap', name: 'Data Dependency Map', type: 'graph' }],
    configSchema: [
      { key: 'includeFiles', label: 'Include File I/O', type: 'boolean', default: true, section: 'Options' },
      { key: 'includeDB', label: 'Include Database', type: 'boolean', default: true, section: 'Options' },
      { key: 'includeQueues', label: 'Include MQ/Queues', type: 'boolean', default: true, section: 'Options' },
    ],
    defaultConfig: { includeFiles: true, includeDB: true, includeQueues: true },
  },
  'map-external': {
    type: 'map-external',
    disabled: true,
    category: 'map',
    label: 'Map External Systems',
    description: 'Map connections to external systems, APIs, services',
    icon: 'Globe',
    color: CATEGORY_COLORS.map,
    disabled: true,  // Future: dedicated external system mapper
    inputs: [{ id: 'ast', name: 'Parsed AST', type: 'ast', required: true }],
    outputs: [{ id: 'externalMap', name: 'External System Map', type: 'graph' }],
    configSchema: [
      { key: 'detectAPIs', label: 'Detect API Calls', type: 'boolean', default: true, section: 'Options' },
      { key: 'detectCICS', label: 'Detect CICS Transactions', type: 'boolean', default: true, section: 'Options' },
    ],
    defaultConfig: { detectAPIs: true, detectCICS: true },
  },

  // ===========================================================================
  // DECOMPOSE - Monolith Identifier: boundaries, clusters, cut points
  // ===========================================================================
  'monolith-identifier': {
    type: 'monolith-identifier',
    disabled: true,
    category: 'decompose',
    label: 'Monolith Identifier',
    description: 'Detect anti-patterns and decomposition opportunities',
    icon: 'Scissors',
    color: CATEGORY_COLORS.decompose,
    apiEndpoint: '/monolithidentifier',
    apiMethod: 'POST',
    inputs: [{ id: 'analysis', name: 'Analysis Results', type: 'json', required: true }],
    outputs: [
      { id: 'patterns', name: 'Detected Patterns', type: 'json' },
      { id: 'decomposition', name: 'Decomposition Strategy', type: 'json' },
    ],
    configSchema: [
      { key: 'analysis_type', label: 'Analysis Type', type: 'select', options: [
        { value: 'cobol', label: 'COBOL Analysis' },
        { value: 'java', label: 'Java Analysis' },
      ], default: 'cobol', section: 'General' },
    ],
    defaultConfig: { analysis_type: 'cobol' },
  },
  'identify-boundaries': {
    type: 'identify-boundaries',
    disabled: true,
    category: 'decompose',
    label: 'Identify Boundaries',
    description: 'Find candidate service/module boundaries',
    icon: 'LayoutGrid',
    color: CATEGORY_COLORS.decompose,
    disabled: true,  // Future: dedicated boundary detection
    inputs: [
      { id: 'callGraph', name: 'Call Graph', type: 'graph', required: true },
      { id: 'dataMap', name: 'Data Map', type: 'graph' },
    ],
    outputs: [{ id: 'boundaries', name: 'Boundary Candidates', type: 'json' }],
    configSchema: [
      { key: 'strategy', label: 'Decomposition Strategy', type: 'select', options: [
        { value: 'domain', label: 'Domain-Driven' },
        { value: 'data', label: 'Data-Centric' },
        { value: 'coupling', label: 'Low-Coupling' },
        { value: 'auto', label: 'Auto-detect' },
      ], default: 'auto', section: 'General' },
      { key: 'minSize', label: 'Min Service Size', type: 'number', default: 3, section: 'Options' },
    ],
    defaultConfig: { strategy: 'auto', minSize: 3 },
  },
  'find-clusters': {
    type: 'find-clusters',
    disabled: true,
    category: 'decompose',
    label: 'Find Clusters',
    description: 'Identify highly-coupled code clusters',
    icon: 'CircleDot',
    color: CATEGORY_COLORS.decompose,
    disabled: true,  // Future: dedicated cluster detection
    inputs: [{ id: 'callGraph', name: 'Call Graph', type: 'graph', required: true }],
    outputs: [{ id: 'clusters', name: 'Code Clusters', type: 'json' }],
    configSchema: [
      { key: 'algorithm', label: 'Clustering Algorithm', type: 'select', options: [
        { value: 'louvain', label: 'Louvain' },
        { value: 'spectral', label: 'Spectral' },
        { value: 'hierarchical', label: 'Hierarchical' },
      ], default: 'louvain', section: 'General' },
      { key: 'threshold', label: 'Coupling Threshold', type: 'number', default: 0.5, section: 'Options' },
    ],
    defaultConfig: { algorithm: 'louvain', threshold: 0.5 },
  },
  'detect-hotspots': {
    type: 'detect-hotspots',
    disabled: true,
    category: 'decompose',
    label: 'Detect Hotspots',
    description: 'Find high-risk areas, shared utilities, choke points',
    icon: 'Flame',
    color: CATEGORY_COLORS.decompose,
    disabled: true,  // Future: dedicated hotspot detection
    inputs: [
      { id: 'callGraph', name: 'Call Graph', type: 'graph', required: true },
      { id: 'metrics', name: 'Metrics', type: 'json' },
    ],
    outputs: [{ id: 'hotspots', name: 'Hotspot Report', type: 'json' }],
    configSchema: [
      { key: 'complexityWeight', label: 'Complexity Weight', type: 'number', default: 0.4, section: 'Weights' },
      { key: 'couplingWeight', label: 'Coupling Weight', type: 'number', default: 0.3, section: 'Weights' },
      { key: 'changeFreqWeight', label: 'Change Frequency Weight', type: 'number', default: 0.3, section: 'Weights' },
    ],
    defaultConfig: { complexityWeight: 0.4, couplingWeight: 0.3, changeFreqWeight: 0.3 },
  },
  'suggest-cuts': {
    type: 'suggest-cuts',
    disabled: true,
    category: 'decompose',
    label: 'Suggest Cut Lines',
    description: 'Recommend where to split the monolith',
    icon: 'Scissors',
    color: CATEGORY_COLORS.decompose,
    disabled: true,  // Future: dedicated cut suggestion
    inputs: [
      { id: 'boundaries', name: 'Boundaries', type: 'json', required: true },
      { id: 'clusters', name: 'Clusters', type: 'json' },
      { id: 'hotspots', name: 'Hotspots', type: 'json' },
    ],
    outputs: [{ id: 'cuts', name: 'Cut Recommendations', type: 'json' }],
    configSchema: [
      { key: 'maxServices', label: 'Max Target Services', type: 'number', default: 10, section: 'Options' },
      { key: 'preserveTransactions', label: 'Preserve Transaction Boundaries', type: 'boolean', default: true, section: 'Options' },
    ],
    defaultConfig: { maxServices: 10, preserveTransactions: true },
  },

  // ===========================================================================
  // DATA - Data Analysis: structures, layouts, schemas, entities
  // ===========================================================================
  'data-analysis': {
    type: 'data-analysis',
    disabled: true,
    category: 'data',
    label: 'Data Analysis',
    description: 'Analyze data structures, relationships, and database schemas',
    icon: 'Database',
    color: CATEGORY_COLORS.data,
    apiEndpoint: '/dataanalysis',
    apiMethod: 'POST',
    inputs: [{ id: 'analysis', name: 'Analysis Results', type: 'json', required: true }],
    outputs: [
      { id: 'erd', name: 'ERD', type: 'json' },
      { id: 'lineage', name: 'Data Lineage', type: 'json' },
    ],
    configSchema: [
      { key: 'generate_erd', label: 'Generate ERD', type: 'boolean', default: true, section: 'Options' },
      { key: 'generate_ddl', label: 'Generate DDL', type: 'boolean', default: true, section: 'Options' },
    ],
    defaultConfig: { generate_erd: true, generate_ddl: true },
  },
  'extract-structures': {
    type: 'extract-structures',
    disabled: true,
    category: 'data',
    label: 'Extract Data Structures',
    description: 'Extract record layouts, copybook structures, data definitions',
    icon: 'TableProperties',
    color: CATEGORY_COLORS.data,
    disabled: true,  // Future: dedicated structure extraction
    inputs: [{ id: 'ast', name: 'Parsed AST', type: 'ast', required: true }],
    outputs: [{ id: 'structures', name: 'Data Structures', type: 'json' }],
    configSchema: [
      { key: 'includeRedefines', label: 'Include REDEFINES', type: 'boolean', default: true, section: 'Options' },
      { key: 'expandOccurs', label: 'Expand OCCURS', type: 'boolean', default: false, section: 'Options' },
    ],
    defaultConfig: { includeRedefines: true, expandOccurs: false },
  },
  'analyze-schema': {
    type: 'analyze-schema',
    disabled: true,
    category: 'data',
    label: 'Analyze Schema',
    description: 'Analyze database schema usage and relationships',
    icon: 'Database',
    color: CATEGORY_COLORS.data,
    disabled: true,  // Future: dedicated schema analysis
    inputs: [{ id: 'sql', name: 'SQL Analysis', type: 'json', required: true }],
    outputs: [{ id: 'schema', name: 'Schema Analysis', type: 'json' }],
    configSchema: [
      { key: 'inferRelationships', label: 'Infer Relationships', type: 'boolean', default: true, section: 'Options' },
      { key: 'detectNormalization', label: 'Detect Normalization Issues', type: 'boolean', default: true, section: 'Options' },
    ],
    defaultConfig: { inferRelationships: true, detectNormalization: true },
  },
  'model-entities': {
    type: 'model-entities',
    disabled: true,
    category: 'data',
    label: 'Model Entities',
    description: 'Generate entity candidates from data analysis',
    icon: 'Boxes',
    color: CATEGORY_COLORS.data,
    disabled: true,  // Future: dedicated entity modeling
    inputs: [
      { id: 'structures', name: 'Data Structures', type: 'json', required: true },
      { id: 'schema', name: 'Schema Analysis', type: 'json' },
    ],
    outputs: [{ id: 'entities', name: 'Entity Model', type: 'json' }],
    configSchema: [
      { key: 'namingStrategy', label: 'Entity Naming', type: 'select', options: [
        { value: 'domain', label: 'Domain-Driven' },
        { value: 'table', label: 'From Table Names' },
        { value: 'ai', label: 'AI-Suggested' },
      ], default: 'domain', section: 'General' },
    ],
    defaultConfig: { namingStrategy: 'domain' },
  },
  'map-fields': {
    type: 'map-fields',
    disabled: true,
    category: 'data',
    label: 'Map Fields',
    description: 'Map source fields to target domain fields',
    icon: 'ArrowLeftRight',
    color: CATEGORY_COLORS.data,
    disabled: true,  // Future: dedicated field mapping
    inputs: [
      { id: 'structures', name: 'Data Structures', type: 'json', required: true },
      { id: 'entities', name: 'Entity Model', type: 'json' },
    ],
    outputs: [{ id: 'fieldMap', name: 'Field Mapping', type: 'json' }],
    configSchema: [
      { key: 'autoMap', label: 'Auto-Map Matching Names', type: 'boolean', default: true, section: 'Options' },
      { key: 'suggestTypes', label: 'Suggest Type Conversions', type: 'boolean', default: true, section: 'Options' },
    ],
    defaultConfig: { autoMap: true, suggestTypes: true },
  },

  // ===========================================================================
  // DISCOVER - Discovery: aggregate into comprehensive app understanding
  // ===========================================================================
  'discovery-roi': {
    type: 'discovery-roi',
    disabled: true,
    category: 'discover',
    label: 'Discovery & ROI',
    description: 'Generate ROI analysis, migration roadmap, and business process discovery',
    icon: 'TrendingUp',
    color: CATEGORY_COLORS.discover,
    apiEndpoint: '/discovery/analyze',
    apiMethod: 'POST',
    inputs: [{ id: 'analysis', name: 'Analysis Results', type: 'json', required: true }],
    outputs: [
      { id: 'roi', name: 'ROI Analysis', type: 'json' },
      { id: 'roadmap', name: 'Migration Roadmap', type: 'json' },
      { id: 'processes', name: 'Business Processes', type: 'json' },
    ],
    configSchema: [
      { key: 'cobol_dev_count', label: 'COBOL Developer Count', type: 'number', default: 5, section: 'ROI Inputs' },
      { key: 'cobol_dev_salary', label: 'Developer Salary ($)', type: 'number', default: 124681, section: 'ROI Inputs' },
      { key: 'discount_rate', label: 'Discount Rate (%)', type: 'number', default: 8, section: 'ROI Inputs' },
      { key: 'mainframe_cost', label: 'Annual Mainframe Cost ($)', type: 'number', default: 50000, section: 'ROI Inputs' },
    ],
    defaultConfig: { cobol_dev_count: 5, cobol_dev_salary: 124681, discount_rate: 8, mainframe_cost: 50000 },
  },
  'summarize-app': {
    type: 'summarize-app',
    disabled: true,
    category: 'discover',
    label: 'Summarize Application',
    description: 'Generate comprehensive application summary',
    icon: 'FileText',
    color: CATEGORY_COLORS.discover,
    disabled: true,  // Future: dedicated app summary
    inputs: [
      { id: 'structure', name: 'Structure', type: 'json' },
      { id: 'metrics', name: 'Metrics', type: 'json' },
      { id: 'callGraph', name: 'Call Graph', type: 'graph' },
    ],
    outputs: [{ id: 'summary', name: 'App Summary', type: 'json' }],
    configSchema: [
      { key: 'includeStats', label: 'Include Statistics', type: 'boolean', default: true, section: 'Options' },
      { key: 'aiEnhanced', label: 'AI-Enhanced Description', type: 'boolean', default: true, section: 'Options' },
    ],
    defaultConfig: { includeStats: true, aiEnhanced: true },
  },
  'document-flows': {
    type: 'document-flows',
    disabled: true,
    category: 'discover',
    label: 'Document Business Flows',
    description: 'Extract and document business process flows',
    icon: 'Workflow',
    color: CATEGORY_COLORS.discover,
    disabled: true,  // Future: dedicated flow documentation
    inputs: [
      { id: 'controlFlow', name: 'Control Flow', type: 'graph', required: true },
      { id: 'ast', name: 'Parsed AST', type: 'ast' },
    ],
    outputs: [{ id: 'flows', name: 'Business Flows', type: 'json' }],
    configSchema: [
      { key: 'extractRules', label: 'Extract Business Rules', type: 'boolean', default: true, section: 'Options' },
      { key: 'generateDiagrams', label: 'Generate Flow Diagrams', type: 'boolean', default: true, section: 'Options' },
    ],
    defaultConfig: { extractRules: true, generateDiagrams: true },
  },
  'assess-risks': {
    type: 'assess-risks',
    disabled: true,
    category: 'discover',
    label: 'Assess Risks',
    description: 'Identify migration risks and complexity factors',
    icon: 'AlertTriangle',
    color: CATEGORY_COLORS.discover,
    disabled: true,  // Future: dedicated risk assessment
    inputs: [
      { id: 'metrics', name: 'Metrics', type: 'json', required: true },
      { id: 'hotspots', name: 'Hotspots', type: 'json' },
      { id: 'boundaries', name: 'Boundaries', type: 'json' },
    ],
    outputs: [{ id: 'risks', name: 'Risk Assessment', type: 'json' }],
    configSchema: [
      { key: 'riskCategories', label: 'Risk Categories', type: 'select', options: [
        { value: 'all', label: 'All Categories' },
        { value: 'technical', label: 'Technical Only' },
        { value: 'business', label: 'Business Only' },
      ], default: 'all', section: 'General' },
    ],
    defaultConfig: { riskCategories: 'all' },
  },
  'plan-migration': {
    type: 'plan-migration',
    disabled: true,
    category: 'discover',
    label: 'Plan Migration',
    description: 'Generate migration plan and recommendations',
    icon: 'ClipboardList',
    color: CATEGORY_COLORS.discover,
    disabled: true,  // Future: dedicated migration planning
    inputs: [
      { id: 'summary', name: 'App Summary', type: 'json', required: true },
      { id: 'risks', name: 'Risks', type: 'json' },
      { id: 'cuts', name: 'Cut Recommendations', type: 'json' },
    ],
    outputs: [{ id: 'plan', name: 'Migration Plan', type: 'json' }],
    configSchema: [
      { key: 'targetPlatform', label: 'Target Platform', type: 'select', options: [
        { value: 'spring', label: 'Spring Boot' },
        { value: 'quarkus', label: 'Quarkus' },
        { value: 'dotnet', label: '.NET Core' },
        { value: 'node', label: 'Node.js' },
      ], default: 'spring', section: 'General' },
      { key: 'phases', label: 'Number of Phases', type: 'number', default: 3, section: 'Options' },
    ],
    defaultConfig: { targetPlatform: 'spring', phases: 3 },
  },

  // ===========================================================================
  // ARCHITECTURE - Cloud architecture recommendations and IaC
  // ===========================================================================
  'architecture': {
    type: 'architecture',
    disabled: true,
    category: 'architecture',
    label: 'Architecture',
    description: 'Generate AWS recommendations and Infrastructure as Code',
    icon: 'Cloud',
    color: CATEGORY_COLORS.architecture,
    apiEndpoint: '/architecture/analyze',
    apiMethod: 'POST',
    inputs: [{ id: 'analysis', name: 'Analysis Results', type: 'json', required: true }],
    outputs: [
      { id: 'recommendations', name: 'AWS Recommendations', type: 'json' },
      { id: 'iac', name: 'IaC Templates', type: 'files' },
    ],
    configSchema: [
      { key: 'cloud_provider', label: 'Cloud Provider', type: 'select', options: [
        { value: 'aws', label: 'AWS' },
        { value: 'azure', label: 'Azure' },
        { value: 'gcp', label: 'Google Cloud' },
      ], default: 'aws', section: 'General' },
      { key: 'iac_type', label: 'IaC Type', type: 'select', options: [
        { value: 'cdk', label: 'AWS CDK' },
        { value: 'cloudformation', label: 'CloudFormation' },
        { value: 'terraform', label: 'Terraform' },
      ], default: 'cdk', section: 'General' },
    ],
    defaultConfig: { cloud_provider: 'aws', iac_type: 'cdk' },
  },

  // ===========================================================================
  // GENERATE - Code Generation: Java packaging, skeleton, models, services, APIs
  // ===========================================================================
  'service-generation': {
    type: 'service-generation',
    disabled: true,
    category: 'generate',
    label: 'Service Generation',
    description: 'Generate Spring Boot services from analyzed code. Preserves all business logic with full traceability back to COBOL.',
    icon: 'Component',
    color: CATEGORY_COLORS.generate,
    apiEndpoint: '/servicegeneration',
    apiMethod: 'POST',
    inputs: [
      { id: 'codeanalysis', name: 'Code Analysis', type: 'json', required: true },
      { id: 'coderefactor', name: 'Code Refactor', type: 'json', required: false },
      { id: 'dataanalysis', name: 'Data Analysis', type: 'json', required: false },
    ],
    outputs: [
      { id: 'services', name: 'Generated Services', type: 'files' },
      { id: 'traceability', name: 'Traceability Map', type: 'json' },
      { id: 'job_id', name: 'Job ID', type: 'string' },
    ],
    configSchema: [
      { key: 'program_name', label: 'Program Name', type: 'text', placeholder: 'IFPR321 (auto-detected from analysis)', section: 'General' },
      { key: 'base_package', label: 'Base Package', type: 'text', default: 'com.modernizeit.generated', section: 'General' },
      { key: 'include_all_repositories', label: 'Include All Repositories', type: 'boolean', default: true, section: 'Dependencies', description: 'Inject all ERD repositories regardless of detected usage' },
      { key: 'include_all_fields', label: 'Include All Fields', type: 'boolean', default: true, section: 'Dependencies', description: 'Include all fields from monolith regardless of detected usage' },
      { key: 'strict_validation', label: 'Strict Validation', type: 'boolean', default: true, section: 'Validation', description: 'Fail if any method body is empty or contains TODO stubs' },
    ],
    defaultConfig: { base_package: 'com.modernizeit.generated', include_all_repositories: true, include_all_fields: true, strict_validation: true },
  },
  'generate-java-package': {
    type: 'generate-java-package',
    disabled: true,
    category: 'generate',
    label: 'Java Package',
    description: 'Package Java code into a production-ready Spring Boot application with Docker, tests, and build scripts',
    icon: 'Package',
    color: CATEGORY_COLORS.generate,
    apiEndpoint: '/java-packaging/start',
    apiMethod: 'POST',
    inputs: [{ id: 'refactor', name: 'Refactored Code', type: 'json', required: true }],
    outputs: [
      { id: 'package', name: 'Spring Boot Package', type: 'file' },
      { id: 'validation', name: 'Validation Report', type: 'json' },
    ],
    configSchema: [
      { key: 'source', label: 'Java Source', type: 'select', options: [
        { value: 'refactor', label: 'Code Refactor Output (Recommended)' },
        { value: 'analysis', label: 'Code Analysis Output (Raw)' },
      ], default: 'refactor', section: 'Source' },
      { key: 'spring_boot_version', label: 'Spring Boot Version', type: 'select', options: [
        { value: '3.2.0', label: '3.2.0 (Latest)' },
        { value: '3.1.0', label: '3.1.0' },
        { value: '2.7.18', label: '2.7.18 (LTS)' },
      ], default: '3.2.0', section: 'Framework' },
      { key: 'java_version', label: 'Java Version', type: 'select', options: [
        { value: '21', label: 'Java 21 (LTS)' },
        { value: '17', label: 'Java 17 (LTS)' },
        { value: '11', label: 'Java 11 (LTS)' },
      ], default: '17', section: 'Framework' },
      { key: 'include_docker', label: 'Include Docker Files', type: 'boolean', default: true, section: 'Options' },
      { key: 'include_tests', label: 'Include Test Stubs', type: 'boolean', default: true, section: 'Options' },
      { key: 'run_validation', label: 'Run Validation', type: 'boolean', default: true, section: 'Options' },
    ],
    defaultConfig: { source: 'refactor', spring_boot_version: '3.2.0', java_version: '17', include_docker: true, include_tests: true, run_validation: true },
  },
  'generate-skeleton': {
    type: 'generate-skeleton',
    disabled: true,
    category: 'generate',
    label: 'Generate Skeleton',
    description: 'Generate target application structure and scaffolding',
    icon: 'FolderTree',
    color: CATEGORY_COLORS.generate,
    disabled: true,  // Future: Use Java Package instead
    inputs: [{ id: 'plan', name: 'Migration Plan', type: 'json', required: true }],
    outputs: [{ id: 'skeleton', name: 'App Skeleton', type: 'files' }],
    configSchema: [
      { key: 'framework', label: 'Framework', type: 'select', options: [
        { value: 'spring-boot', label: 'Spring Boot' },
        { value: 'quarkus', label: 'Quarkus' },
        { value: 'micronaut', label: 'Micronaut' },
        { value: 'dotnet', label: '.NET 8' },
      ], default: 'spring-boot', section: 'General' },
      { key: 'buildTool', label: 'Build Tool', type: 'select', options: [
        { value: 'maven', label: 'Maven' },
        { value: 'gradle', label: 'Gradle' },
      ], default: 'maven', section: 'General' },
      { key: 'includeDocker', label: 'Include Docker', type: 'boolean', default: true, section: 'Options' },
    ],
    defaultConfig: { framework: 'spring-boot', buildTool: 'maven', includeDocker: true },
  },
  'generate-models': {
    type: 'generate-models',
    disabled: true,
    category: 'generate',
    label: 'Generate Models',
    description: 'Generate entity classes, DTOs, and data models',
    icon: 'Boxes',
    color: CATEGORY_COLORS.generate,
    disabled: true,  // Future: Use Java Package instead
    inputs: [
      { id: 'entities', name: 'Entity Model', type: 'json', required: true },
      { id: 'fieldMap', name: 'Field Mapping', type: 'json' },
    ],
    outputs: [{ id: 'models', name: 'Model Classes', type: 'files' }],
    configSchema: [
      { key: 'generateDTOs', label: 'Generate DTOs', type: 'boolean', default: true, section: 'Options' },
      { key: 'generateMappers', label: 'Generate Mappers', type: 'boolean', default: true, section: 'Options' },
      { key: 'validation', label: 'Add Validation', type: 'boolean', default: true, section: 'Options' },
    ],
    defaultConfig: { generateDTOs: true, generateMappers: true, validation: true },
  },
  'generate-services': {
    type: 'generate-services',
    disabled: true,
    category: 'generate',
    label: 'Generate Services',
    description: 'Generate service layer with business logic',
    icon: 'Cog',
    color: CATEGORY_COLORS.generate,
    disabled: true,  // Future: Use Java Package instead
    inputs: [
      { id: 'flows', name: 'Business Flows', type: 'json', required: true },
      { id: 'models', name: 'Models', type: 'files' },
    ],
    outputs: [{ id: 'services', name: 'Service Classes', type: 'files' }],
    configSchema: [
      { key: 'transactional', label: 'Add Transaction Support', type: 'boolean', default: true, section: 'Options' },
      { key: 'logging', label: 'Add Logging', type: 'boolean', default: true, section: 'Options' },
    ],
    defaultConfig: { transactional: true, logging: true },
  },
  'generate-apis': {
    type: 'generate-apis',
    disabled: true,
    category: 'generate',
    label: 'Generate APIs',
    description: 'Generate REST controllers and API endpoints',
    icon: 'Plug',
    color: CATEGORY_COLORS.generate,
    disabled: true,  // Future: Use Java Package instead
    inputs: [{ id: 'services', name: 'Services', type: 'files', required: true }],
    outputs: [{ id: 'apis', name: 'API Controllers', type: 'files' }],
    configSchema: [
      { key: 'apiStyle', label: 'API Style', type: 'select', options: [
        { value: 'rest', label: 'REST' },
        { value: 'graphql', label: 'GraphQL' },
        { value: 'grpc', label: 'gRPC' },
      ], default: 'rest', section: 'General' },
      { key: 'generateOpenAPI', label: 'Generate OpenAPI Spec', type: 'boolean', default: true, section: 'Options' },
    ],
    defaultConfig: { apiStyle: 'rest', generateOpenAPI: true },
  },
  'generate-test-stubs': {
    type: 'generate-test-stubs',
    disabled: true,
    category: 'generate',
    label: 'Test Stubs',
    description: 'Generate JUnit test scaffolding with TODOs (no AI, fast)',
    icon: 'TestTube2',
    color: CATEGORY_COLORS.generate,
    apiEndpoint: '/test-generation/stubs',
    apiMethod: 'POST',
    inputs: [{ id: 'java', name: 'Java Code', type: 'json', required: true }],
    outputs: [
      { id: 'tests', name: 'Test Stubs', type: 'files' },
      { id: 'manifest', name: 'Test Manifest', type: 'json' },
    ],
    configSchema: [
      { key: 'source', label: 'Java Source', type: 'select', options: [
        { value: 'refactor', label: 'Code Refactor Output (Recommended)' },
        { value: 'analysis', label: 'Code Analysis Output (Raw)' },
      ], default: 'refactor', section: 'Source' },
      { key: 'framework', label: 'Test Framework', type: 'select', options: [
        { value: 'junit5', label: 'JUnit 5' },
        { value: 'testng', label: 'TestNG' },
      ], default: 'junit5', section: 'Framework' },
      { key: 'include_mocks', label: 'Include Mockito Setup', type: 'boolean', default: true, section: 'Options' },
      { key: 'fail_on_todo', label: 'Tests Fail Until Implemented', type: 'boolean', default: true, section: 'Options' },
    ],
    defaultConfig: { source: 'refactor', framework: 'junit5', include_mocks: true, fail_on_todo: true },
  },
  'generate-smart-tests': {
    type: 'generate-smart-tests',
    disabled: true,
    category: 'generate',
    label: 'Smart Tests',
    description: 'Generate meaningful JUnit tests using AI with real assertions from COBOL logic',
    icon: 'Sparkles',
    color: CATEGORY_COLORS.generate,
    apiEndpoint: '/test-generation/smart',
    apiMethod: 'POST',
    inputs: [{ id: 'java', name: 'Java Code', type: 'json', required: true }],
    outputs: [
      { id: 'tests', name: 'Smart Tests', type: 'files' },
      { id: 'manifest', name: 'Test Manifest', type: 'json' },
    ],
    configSchema: [
      { key: 'source', label: 'Java Source', type: 'select', options: [
        { value: 'refactor', label: 'Code Refactor Output (Recommended)' },
        { value: 'analysis', label: 'Code Analysis Output (Raw)' },
      ], default: 'refactor', section: 'Source' },
      { key: 'framework', label: 'Test Framework', type: 'select', options: [
        { value: 'junit5', label: 'JUnit 5' },
        { value: 'testng', label: 'TestNG' },
      ], default: 'junit5', section: 'Framework' },
      { key: 'mock_framework', label: 'Mock Framework', type: 'select', options: [
        { value: 'mockito', label: 'Mockito' },
        { value: 'mockk', label: 'MockK' },
      ], default: 'mockito', section: 'Framework' },
      { key: 'unit_tests', label: 'Generate Unit Tests', type: 'boolean', default: true, section: 'Test Types' },
      { key: 'validation_tests', label: 'Generate Validation Tests (from Copybook)', type: 'boolean', default: true, section: 'Test Types' },
      { key: 'integration_tests', label: 'Generate Integration Tests', type: 'boolean', default: false, section: 'Test Types' },
      { key: 'coverage_target', label: 'Coverage Target', type: 'select', options: [
        { value: '60', label: '60% (Basic)' },
        { value: '80', label: '80% (Standard)' },
        { value: '100', label: '100% (Full)' },
      ], default: '80', section: 'Options' },
      { key: 'max_ai_calls', label: 'Max AI Calls (Cost Control)', type: 'number', default: 50, section: 'Options' },
    ],
    defaultConfig: { source: 'refactor', framework: 'junit5', mock_framework: 'mockito', unit_tests: true, validation_tests: true, integration_tests: false, coverage_target: '80', max_ai_calls: 50 },
  },

  // ===========================================================================
  // OUTPUT - Export, reports, checkpoints
  // ===========================================================================
  'export-package': {
    type: 'export-package',
    disabled: true,
    category: 'output',
    label: 'Export Package',
    description: 'Package all outputs into downloadable archive',
    icon: 'Archive',
    color: CATEGORY_COLORS.output,
    inputs: [{ id: 'files', name: 'Generated Files', type: 'files', required: true }],
    outputs: [{ id: 'package', name: 'Package', type: 'file' }],
    configSchema: [
      { key: 'format', label: 'Package Format', type: 'select', options: [
        { value: 'zip', label: 'ZIP Archive' },
        { value: 'tar.gz', label: 'TAR.GZ' },
      ], default: 'zip', section: 'General' },
      { key: 'includeReports', label: 'Include Reports', type: 'boolean', default: true, section: 'Options' },
    ],
    defaultConfig: { format: 'zip', includeReports: true },
  },
  'generate-report': {
    type: 'generate-report',
    disabled: true,
    category: 'output',
    label: 'Generate Report',
    description: 'Generate comprehensive analysis/migration report',
    icon: 'FileSpreadsheet',
    color: CATEGORY_COLORS.output,
    inputs: [
      { id: 'summary', name: 'Summary', type: 'json' },
      { id: 'metrics', name: 'Metrics', type: 'json' },
      { id: 'risks', name: 'Risks', type: 'json' },
      { id: 'plan', name: 'Plan', type: 'json' },
    ],
    outputs: [{ id: 'report', name: 'Report', type: 'file' }],
    configSchema: [
      { key: 'format', label: 'Report Format', type: 'select', options: [
        { value: 'pdf', label: 'PDF' },
        { value: 'html', label: 'HTML' },
        { value: 'docx', label: 'Word Document' },
      ], default: 'pdf', section: 'General' },
      { key: 'template', label: 'Report Template', type: 'select', options: [
        { value: 'executive', label: 'Executive Summary' },
        { value: 'technical', label: 'Technical Detail' },
        { value: 'full', label: 'Full Report' },
      ], default: 'full', section: 'General' },
    ],
    defaultConfig: { format: 'pdf', template: 'full' },
  },

  // ===========================================================================
  // CONTROL - Workflow control and flow management
  // ===========================================================================
  'control.pause': {
    type: 'control.pause',
    category: 'control',
    label: 'Pause',
    description: 'Pause workflow execution for manual intervention (e.g., edit artifacts, review results)',
    icon: 'Pause',
    color: CATEGORY_COLORS.control,
    inputs: [{ id: 'trigger', name: 'Input', type: 'any', required: false }],
    outputs: [{ id: 'continue', name: 'Output', type: 'any' }],
    configSchema: [
      { key: 'message', label: 'Pause Message', type: 'text', default: 'Workflow paused - click Resume to continue', section: 'General' },
    ],
    defaultConfig: { message: 'Workflow paused - click Resume to continue' },
  },
  'human-approval': {
    type: 'human-approval',
    disabled: true,
    category: 'output',
    label: 'Human Approval',
    description: 'Checkpoint requiring human review and approval',
    icon: 'UserCheck',
    color: CATEGORY_COLORS.output,
    inputs: [{ id: 'data', name: 'Data to Review', type: 'any', required: true }],
    outputs: [{ id: 'approved', name: 'Approved Data', type: 'any' }],
    configSchema: [
      { key: 'title', label: 'Review Title', type: 'text', default: 'Review Required', section: 'General' },
      { key: 'instructions', label: 'Instructions', type: 'textarea', default: 'Please review the results and approve to continue.', section: 'General' },
      { key: 'notifyEmail', label: 'Notification Email', type: 'text', default: '', section: 'Notifications' },
    ],
    defaultConfig: { title: 'Review Required', instructions: 'Please review the results and approve to continue.', notifyEmail: '' },
  },

  // ===========================================================================
  // AI - AI-powered transformation, analysis, and assistance
  // ===========================================================================
  // Context Source Options:
  //   - flow_input: Use code/data from upstream node connections
  //   - specific_files: Analyze specific files matching a pattern
  //   - project_files: Full access to all project source files and artifacts
  //   - mcp_tools: Use MCP server tools (requires MCP running)
  // ===========================================================================
  'ai-transform': {
    type: 'ai-transform',
    disabled: true,
    category: 'ai',
    label: 'AI Transform',
    description: 'Send code to AI for intelligent transformation. Can use flow input, specific files, or full project context.',
    icon: 'Sparkles',
    color: CATEGORY_COLORS.ai,
    disabled: true, // Use the AI Assistant panel instead
    apiEndpoint: '/ai/flow',
    apiMethod: 'POST',
    inputs: [
      { id: 'code', name: 'Source Code', type: 'code', required: false },
      { id: 'context', name: 'Context', type: 'json' },
    ],
    outputs: [{ id: 'transformed', name: 'Transformed Code', type: 'code' }],
    configSchema: [
      // Context Source - determines what code the AI analyzes
      { key: 'contextSource', label: 'Context Source', type: 'select', options: [
        { value: 'flow_input', label: 'Flow Input (from upstream node)' },
        { value: 'specific_files', label: 'Specific Files (pattern match)' },
        { value: 'project_files', label: 'All Project Files' },
        { value: 'mcp_tools', label: 'MCP Tools (requires MCP running)' },
      ], default: 'flow_input', section: 'Context', description: 'Where the AI gets the code to analyze' },
      { key: 'filePattern', label: 'File Pattern', type: 'text', placeholder: '*.java, *.cbl', section: 'Context', showWhen: { contextSource: 'specific_files' }, description: 'Glob pattern for files to include' },
      // Provider settings
      { key: 'provider', label: 'AI Provider', type: 'select', options: [
        { value: 'bedrock', label: 'AWS Bedrock (Claude)' },
      ], default: 'bedrock', section: 'Provider' },
      { key: 'model', label: 'Model', type: 'select', options: [
        { value: 'claude-sonnet-4-20250514', label: 'Claude Sonnet 4' },
        { value: 'claude-3-5-haiku-20241022', label: 'Claude 3.5 Haiku' },
      ], default: 'claude-sonnet-4-20250514', section: 'Provider' },
      // Transformation settings
      { key: 'prompt', label: 'Transformation Prompt', type: 'textarea', default: 'Transform this code to modern, idiomatic style while preserving functionality.', section: 'Prompt' },
      { key: 'targetLanguage', label: 'Target Language', type: 'select', options: [
        { value: 'java', label: 'Java' },
        { value: 'csharp', label: 'C#' },
        { value: 'python', label: 'Python' },
        { value: 'typescript', label: 'TypeScript' },
        { value: 'same', label: 'Same as Source' },
      ], default: 'java', section: 'Transformation' },
      { key: 'temperature', label: 'Temperature', type: 'number', default: 0.2, section: 'Advanced' },
      { key: 'maxTokens', label: 'Max Tokens', type: 'number', default: 4096, section: 'Advanced' },
    ],
    defaultConfig: { contextSource: 'flow_input', provider: 'bedrock', model: 'claude-sonnet-4-20250514', prompt: 'Transform this code to modern, idiomatic style while preserving functionality.', targetLanguage: 'java', temperature: 0.2, maxTokens: 4096 },
  },
  'ai-explain': {
    type: 'ai-explain',
    disabled: true,
    category: 'ai',
    label: 'AI Explain',
    description: 'Generate AI-powered documentation and explanations for code.',
    icon: 'MessageSquareText',
    color: CATEGORY_COLORS.ai,
    disabled: true, // Use the AI Assistant panel instead
    apiEndpoint: '/ai/flow',
    apiMethod: 'POST',
    inputs: [
      { id: 'code', name: 'Source Code', type: 'code', required: false },
      { id: 'structure', name: 'Structure Analysis', type: 'json' },
    ],
    outputs: [{ id: 'documentation', name: 'Documentation', type: 'markdown' }],
    configSchema: [
      // Context Source
      { key: 'contextSource', label: 'Context Source', type: 'select', options: [
        { value: 'flow_input', label: 'Flow Input (from upstream node)' },
        { value: 'specific_files', label: 'Specific Files (pattern match)' },
        { value: 'project_files', label: 'All Project Files' },
        { value: 'mcp_tools', label: 'MCP Tools (requires MCP running)' },
      ], default: 'flow_input', section: 'Context' },
      { key: 'filePattern', label: 'File Pattern', type: 'text', placeholder: '*.java, *.cbl', section: 'Context', showWhen: { contextSource: 'specific_files' } },
      // Provider
      { key: 'provider', label: 'AI Provider', type: 'select', options: [
        { value: 'bedrock', label: 'AWS Bedrock (Claude)' },
      ], default: 'bedrock', section: 'Provider' },
      { key: 'model', label: 'Model', type: 'select', options: [
        { value: 'claude-sonnet-4-20250514', label: 'Claude Sonnet 4' },
        { value: 'claude-3-5-haiku-20241022', label: 'Claude 3.5 Haiku' },
      ], default: 'claude-sonnet-4-20250514', section: 'Provider' },
      // Output settings
      { key: 'outputFormat', label: 'Output Format', type: 'select', options: [
        { value: 'markdown', label: 'Markdown' },
        { value: 'html', label: 'HTML' },
        { value: 'jsdoc', label: 'JSDoc/Javadoc' },
        { value: 'plain', label: 'Plain Text' },
      ], default: 'markdown', section: 'Output' },
      { key: 'detailLevel', label: 'Detail Level', type: 'select', options: [
        { value: 'summary', label: 'Summary (High-level overview)' },
        { value: 'standard', label: 'Standard (Functions + logic)' },
        { value: 'detailed', label: 'Detailed (Line-by-line)' },
      ], default: 'standard', section: 'Output' },
      { key: 'includeExamples', label: 'Include Usage Examples', type: 'boolean', default: true, section: 'Options' },
      { key: 'audienceLevel', label: 'Target Audience', type: 'select', options: [
        { value: 'developer', label: 'Developer' },
        { value: 'architect', label: 'Architect' },
        { value: 'business', label: 'Business Stakeholder' },
      ], default: 'developer', section: 'Options' },
    ],
    defaultConfig: { contextSource: 'flow_input', provider: 'bedrock', model: 'claude-sonnet-4-20250514', outputFormat: 'markdown', detailLevel: 'standard', includeExamples: true, audienceLevel: 'developer' },
  },
  'ai-review': {
    type: 'ai-review',
    disabled: true,
    category: 'ai',
    label: 'AI Review',
    description: 'AI-powered code review with actionable suggestions.',
    icon: 'ScanSearch',
    color: CATEGORY_COLORS.ai,
    disabled: true, // Use the AI Assistant panel instead
    apiEndpoint: '/ai/flow',
    apiMethod: 'POST',
    inputs: [
      { id: 'code', name: 'Source Code', type: 'code', required: false },
      { id: 'metrics', name: 'Metrics', type: 'json' },
    ],
    outputs: [{ id: 'review', name: 'Code Review', type: 'json' }],
    configSchema: [
      // Context Source
      { key: 'contextSource', label: 'Context Source', type: 'select', options: [
        { value: 'flow_input', label: 'Flow Input (from upstream node)' },
        { value: 'specific_files', label: 'Specific Files (pattern match)' },
        { value: 'project_files', label: 'All Project Files' },
        { value: 'mcp_tools', label: 'MCP Tools (requires MCP running)' },
      ], default: 'flow_input', section: 'Context' },
      { key: 'filePattern', label: 'File Pattern', type: 'text', placeholder: '*.java, *.cbl', section: 'Context', showWhen: { contextSource: 'specific_files' } },
      // Provider
      { key: 'provider', label: 'AI Provider', type: 'select', options: [
        { value: 'bedrock', label: 'AWS Bedrock (Claude)' },
      ], default: 'bedrock', section: 'Provider' },
      { key: 'model', label: 'Model', type: 'select', options: [
        { value: 'claude-sonnet-4-20250514', label: 'Claude Sonnet 4' },
        { value: 'claude-3-5-haiku-20241022', label: 'Claude 3.5 Haiku' },
      ], default: 'claude-sonnet-4-20250514', section: 'Provider' },
      // Review settings
      { key: 'reviewFocus', label: 'Review Focus', type: 'select', options: [
        { value: 'all', label: 'Comprehensive' },
        { value: 'security', label: 'Security Issues' },
        { value: 'performance', label: 'Performance' },
        { value: 'maintainability', label: 'Maintainability' },
        { value: 'bugs', label: 'Bug Detection' },
      ], default: 'all', section: 'Review' },
      { key: 'severity', label: 'Min Severity to Report', type: 'select', options: [
        { value: 'info', label: 'Info (All findings)' },
        { value: 'warning', label: 'Warning and above' },
        { value: 'error', label: 'Errors only' },
      ], default: 'info', section: 'Review' },
      { key: 'suggestFixes', label: 'Suggest Fixes', type: 'boolean', default: true, section: 'Options' },
      { key: 'includeRationale', label: 'Include Rationale', type: 'boolean', default: true, section: 'Options' },
    ],
    defaultConfig: { contextSource: 'flow_input', provider: 'bedrock', model: 'claude-sonnet-4-20250514', reviewFocus: 'all', severity: 'info', suggestFixes: true, includeRationale: true },
  },
  'ai-chat': {
    type: 'ai-chat',
    disabled: true,
    category: 'ai',
    label: 'AI Chat',
    description: 'Interactive AI chat about the codebase. Use MCP Tools for full project exploration.',
    icon: 'BotMessageSquare',
    color: CATEGORY_COLORS.ai,
    disabled: true, // Use the AI Assistant panel instead
    apiEndpoint: '/ai/mcp-chat',
    apiMethod: 'POST',
    inputs: [
      { id: 'code', name: 'Code Context', type: 'code', required: false },
      { id: 'analysis', name: 'Analysis Data', type: 'json' },
    ],
    outputs: [{ id: 'response', name: 'AI Response', type: 'text' }],
    configSchema: [
      // Context Source
      { key: 'contextSource', label: 'Context Source', type: 'select', options: [
        { value: 'flow_input', label: 'Flow Input (from upstream node)' },
        { value: 'project_files', label: 'All Project Files' },
        { value: 'mcp_tools', label: 'MCP Tools (requires MCP running)' },
      ], default: 'mcp_tools', section: 'Context', description: 'MCP Tools provides the richest context with file browsing and custom data sources' },
      // Provider
      { key: 'provider', label: 'AI Provider', type: 'select', options: [
        { value: 'bedrock', label: 'AWS Bedrock (Claude)' },
      ], default: 'bedrock', section: 'Provider' },
      { key: 'model', label: 'Model', type: 'select', options: [
        { value: 'claude-sonnet-4-20250514', label: 'Claude Sonnet 4' },
        { value: 'claude-3-5-haiku-20241022', label: 'Claude 3.5 Haiku' },
      ], default: 'claude-sonnet-4-20250514', section: 'Provider' },
      // Chat settings
      { key: 'systemPrompt', label: 'System Prompt', type: 'textarea', default: 'You are an expert code analyst. Help the user understand the provided codebase, answer questions, and provide insights.', section: 'Prompt' },
      { key: 'message', label: 'Initial Message', type: 'textarea', placeholder: 'What would you like to know about the code?', section: 'Prompt', description: 'The question or task to send to the AI' },
      { key: 'persistHistory', label: 'Persist Chat History', type: 'boolean', default: true, section: 'Options' },
      { key: 'maxTurns', label: 'Max Conversation Turns', type: 'number', default: 50, section: 'Options' },
    ],
    defaultConfig: { contextSource: 'mcp_tools', provider: 'bedrock', model: 'claude-sonnet-4-20250514', systemPrompt: 'You are an expert code analyst. Help the user understand the provided codebase, answer questions, and provide insights.', message: '', persistHistory: true, maxTurns: 50 },
  },
  'ai-naming': {
    type: 'ai-naming',
    disabled: true,
    category: 'ai',
    label: 'Semantic Naming',
    description: 'AI-powered semantic naming for methods and services. Infers business meaning from cryptic COBOL paragraph names and suggests readable Java names.',
    icon: 'Tag',
    color: CATEGORY_COLORS.ai,
    apiEndpoint: '/ainaming',
    apiMethod: 'POST',
    inputs: [{ id: 'analysis', name: 'Code Analysis Results', type: 'json', required: true }],
    outputs: [
      { id: 'naming_suggestions', name: 'Naming Suggestions', type: 'json' },
      { id: 'cobol_mapping', name: 'COBOL Traceability', type: 'json' },
    ],
    configSchema: [
      { key: 'enable_ai', label: 'Enable AI Naming', type: 'boolean', default: true, section: 'General', description: 'Use AI to suggest semantic method/service names' },
      { key: 'confidence_threshold', label: 'Confidence Threshold', type: 'number', default: 0.75, min: 0.0, max: 1.0, step: 0.05, section: 'Options', description: 'Minimum confidence score (0.0-1.0)' },
      { key: 'auto_approve_high_confidence', label: 'Auto-approve High Confidence (>0.90)', type: 'boolean', default: false, section: 'Options', description: 'Automatically approve suggestions with confidence > 0.90' },
    ],
    defaultConfig: { enable_ai: true, confidence_threshold: 0.75, auto_approve_high_confidence: false },
  },
}

// =============================================================================
// CATEGORY DEFINITIONS
// =============================================================================

export const NODE_CATEGORIES = {
  templates: {
    label: 'Templates',
    description: 'Pre-built workflow templates',
    icon: 'LayoutTemplate',
    color: CATEGORY_COLORS.templates,
    nodes: [
      'template-full-pipeline',
      'template-idms-full-path',
    ],
  },
  pipeline: {
    label: 'Pipeline',
    description: 'Pre-configured transformation stages',
    icon: 'Workflow',
    color: CATEGORY_COLORS.pipeline,
    nodes: [
      'pipeline-upload',
      'pipeline-code-analysis',
      'pipeline-refactor',
      'pipeline-ai-naming',
      'pipeline-dependencies',
      'pipeline-monolith',
      'pipeline-data',
      'pipeline-discovery',
      'pipeline-architecture',
      'pipeline-business-logic',
      'pipeline-code-translation',
      'pipeline-service-gen',
      'pipeline-java-analysis',
      'pipeline-code-refactor-v2',
      'pipeline-smart-tests',
      'pipeline-java-package',
    ],
  },
  ingest: {
    label: 'Ingest',
    description: 'Load and stage source files',
    icon: 'Upload',
    color: CATEGORY_COLORS.ingest,
    nodes: ['upload-files', 'upload-single', 'git-import'],
  },
  parse: {
    label: 'Parse',
    description: 'Parse source and build structure',
    icon: 'FileCode',
    color: CATEGORY_COLORS.parse,
    nodes: ['parse-source', 'resolve-includes', 'build-symbols'],
  },
  analyze: {
    label: 'Analyze',
    description: 'Code analysis and metrics',
    icon: 'BarChart3',
    color: CATEGORY_COLORS.analyze,
    nodes: ['code-analysis', 'java-analysis', 'business-logic', 'structure-analysis', 'control-flow', 'variable-analysis', 'sql-analysis', 'complexity-metrics'],
  },
  refactor: {
    label: 'Refactor',
    description: 'Code refactoring and cleanup',
    icon: 'Wand2',
    color: CATEGORY_COLORS.refactor,
    nodes: ['code-refactor', 'code-refactor-v2', 'apply-patterns', 'simplify-code', 'standardize-code', 'extract-functions'],
  },
  map: {
    label: 'Map',
    description: 'Dependency mapping',
    icon: 'GitFork',
    color: CATEGORY_COLORS.map,
    nodes: ['dependency-mapper', 'build-call-graph', 'map-includes', 'map-data-deps', 'map-external'],
  },
  decompose: {
    label: 'Decompose',
    description: 'Monolith decomposition',
    icon: 'Scissors',
    color: CATEGORY_COLORS.decompose,
    nodes: ['monolith-identifier', 'identify-boundaries', 'find-clusters', 'detect-hotspots', 'suggest-cuts'],
  },
  data: {
    label: 'Data',
    description: 'Data structure analysis',
    icon: 'Database',
    color: CATEGORY_COLORS.data,
    nodes: ['data-analysis', 'extract-structures', 'analyze-schema', 'model-entities', 'map-fields'],
  },
  discover: {
    label: 'Discover',
    description: 'Application discovery',
    icon: 'Search',
    color: CATEGORY_COLORS.discover,
    nodes: ['discovery-roi', 'summarize-app', 'document-flows', 'assess-risks', 'plan-migration'],
  },
  architecture: {
    label: 'Architecture',
    description: 'Cloud architecture and IaC',
    icon: 'Cloud',
    color: CATEGORY_COLORS.architecture,
    nodes: ['architecture'],
  },
  generate: {
    label: 'Generate',
    description: 'Code generation',
    icon: 'Code2',
    color: CATEGORY_COLORS.generate,
    nodes: ['code-translation', 'service-generation', 'generate-java-package', 'generate-test-stubs', 'generate-smart-tests', 'generate-skeleton', 'generate-models', 'generate-services', 'generate-apis'],
  },
  output: {
    label: 'Output',
    description: 'Export and reports',
    icon: 'Download',
    color: CATEGORY_COLORS.output,
    nodes: ['export-package', 'generate-report', 'human-approval'],
  },
  ai: {
    label: 'AI',
    description: 'AI-powered operations',
    icon: 'Sparkles',
    color: CATEGORY_COLORS.ai,
    nodes: ['ai-transform', 'ai-explain', 'ai-review', 'ai-chat'],
  },
  control: {
    label: 'Control',
    description: 'Workflow control and utilities',
    icon: 'Settings2',
    color: CATEGORY_COLORS.control,
    nodes: ['control.pause', 'wait', 'condition', 'loop', 'parallel', 'checkpoint', 'notify', 'custom-script'],
  },
}

// =============================================================================
// TEMPLATE GENERATORS
// =============================================================================

/**
 * Generate nodes and edges for a template
 * @param {string} templateType - The template type (e.g., 'template-full-pipeline')
 * @param {number} startX - Starting X position on canvas
 * @param {number} startY - Starting Y position on canvas
 * @returns {{ nodes: Array, edges: Array }} The generated nodes and edges
 */
export function generateTemplateNodes(templateType, startX = 100, startY = 100) {
  const template = NODE_REGISTRY[templateType]
  if (!template || !template.isTemplate || !template.templateNodes) {
    return { nodes: [], edges: [] }
  }

  const timestamp = Date.now()
  const nodeSpacingX = 280  // Horizontal spacing between nodes
  const nodes = []
  const edges = []

  // Generate nodes with positions (horizontal layout left to right)
  template.templateNodes.forEach((nodeType, index) => {
    const nodeDef = NODE_REGISTRY[nodeType]
    if (!nodeDef) return

    const nodeId = `${nodeType}-${timestamp}-${index}`

    nodes.push({
      id: nodeId,
      type: 'workflowNode',
      disabled: true,
      position: {
        x: startX + (index * nodeSpacingX),
        y: startY
      },
      data: {
        nodeType: nodeType,
        label: nodeDef.label,
        config: { ...nodeDef.defaultConfig },
        enabled: true,
        status: null,
        error: null,
        executionResult: null
      }
    })

    // Create edge to previous node (if not first)
    if (index > 0) {
      const prevNodeId = `${template.templateNodes[index - 1]}-${timestamp}-${index - 1}`
      edges.push({
        id: `e-${prevNodeId}-${nodeId}`,
        type: 'smoothstep',
        disabled: true,
        source: prevNodeId,
        target: nodeId,
        sourceHandle: null,
        targetHandle: null,
        style: {
          stroke: 'var(--border-color)',
          strokeWidth: 2
        },
        animated: false
      })
    }
  })

  return { nodes, edges }
}

/**
 * Check if a node type is a template
 */
export function isTemplateNode(nodeType) {
  const node = NODE_REGISTRY[nodeType]
  return node && node.isTemplate === true
}

export default NODE_REGISTRY
